import { fabric } from 'fabric';
import QRCode from 'qrcode';
import eventBus from './EventBus';
import speech from './SpeechService';
import { TEXT_3D_PRESETS } from './TextEffects3D';

const PX_PER_CM = 96 / 2.54; // ~37.795 px per cm at screen resolution
const MAX_CANVAS_PX = 4096; // max canvas DOM element size in any dimension

class CanvasManager {
  constructor(canvasId, widthCm = 100, heightCm = 50) {
    this.canvasId = canvasId;
    this.widthCm = widthCm;
    this.heightCm = heightCm;
    this.canvas = null;
    this.gridVisible = false;
    this.gridObjects = [];
    this.bleedObjects = [];
    this.bleedCm = 1;
    this.hemObjects = [];
    this.hemCm = 0;
    this.grommetsObjects = [];
    this.grommetsConfig = null;
    this.hohlsaumObjects = [];
    this.hohlsaumConfig = { top: 0, bottom: 0, left: 0, right: 0 };
    this._clipboardObject = null;
    this._collageLayouts = {}; // { collageId: { templateCells, gapMm } }
    this.internalScale = 1; // ratio of actual canvas px to virtual px
    this._warningTimeout = null; // debounce for moving warnings
    this._lastSpeechWarningTime = 0; // speech debounce (max once per 5s)
    this._warningResetTimers = []; // timers for resetting visual warnings
    this._guides = []; // user-placed guidelines (fabric.Line with isGuide=true)
  }

  /** Virtual canvas width in px (full resolution, not capped) */
  get vWidth() { return Math.round(this.widthCm * PX_PER_CM); }
  /** Virtual canvas height in px (full resolution, not capped) */
  get vHeight() { return Math.round(this.heightCm * PX_PER_CM); }

  /** Calculate the internal scale factor to keep canvas DOM element within MAX_CANVAS_PX */
  _calcInternalScale(widthCm, heightCm) {
    const virtualW = widthCm * PX_PER_CM;
    const virtualH = heightCm * PX_PER_CM;
    const maxDim = Math.max(virtualW, virtualH);
    return maxDim > MAX_CANVAS_PX ? MAX_CANVAS_PX / maxDim : 1;
  }

  initCanvas() {
    const virtualW = Math.round(this.widthCm * PX_PER_CM);
    const virtualH = Math.round(this.heightCm * PX_PER_CM);
    this.internalScale = this._calcInternalScale(this.widthCm, this.heightCm);
    const actualW = Math.round(virtualW * this.internalScale);
    const actualH = Math.round(virtualH * this.internalScale);

    this.canvas = new fabric.Canvas(this.canvasId, {
      width: actualW,
      height: actualH,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
      controlsAboveOverlay: true,
      enableRetinaScaling: false,
      uniformScaling: false,
    });

    // Use Fabric's viewport zoom so all object coordinates stay in the virtual (full-res) space
    if (this.internalScale < 1) {
      this.canvas.setZoom(this.internalScale);
    }

    // Mobile touch support
    if (window.innerWidth < 768) {
      this.canvas.allowTouchScrolling = false;
      // Larger touch targets for mobile
      fabric.Object.prototype.cornerSize = 24;
      fabric.Object.prototype.touchCornerSize = 40;
      fabric.Object.prototype.transparentCorners = false;
      fabric.Object.prototype.cornerColor = '#1a56db';
      fabric.Object.prototype.cornerStrokeColor = '#ffffff';
      fabric.Object.prototype.borderColor = '#1a56db';
    }

    this._bindEvents();
    this.drawBleedLines(this.bleedCm);

    // Fabrics inline-Text-Editing KOMPLETT abschalten — bricht den Canvas unter CSS-Zoom.
    // Auf Doppelklick oeffnen wir stattdessen unseren TextEditModal.
    try {
      const noopEnter = function () { return this; };
      if (fabric.IText && fabric.IText.prototype) fabric.IText.prototype.enterEditing = noopEnter;
      if (fabric.Textbox && fabric.Textbox.prototype) fabric.Textbox.prototype.enterEditing = noopEnter;
      if (fabric.Text && fabric.Text.prototype && fabric.Text.prototype.enterEditing) fabric.Text.prototype.enterEditing = noopEnter;
    } catch (e) { /* ignore */ }

    const textTypes = ['i-text', 'text', 'textbox', 'extrusion3DIText', 'outlineStackIText', 'neonIText', 'retroIText'];

    const handleTextDblClick = (e) => {
      const target = e && e.target;
      if (!target) return;
      try {
        if (textTypes.includes(target.type)) {
          eventBus.emit('text:edit-request', { target });
        } else if (target._curvedText) {
          eventBus.emit('text:edit-request', {
            target,
            isCurved: true,
            text: target._curvedText,
            fontSize: target._curvedFontSize,
            fontFamily: target._curvedFontFamily,
            fill: target._curvedFill,
            stroke: target._curvedStroke,
            strokeWidth: target._curvedStrokeWidth,
            curveType: target._curvedType,
          });
        }
      } catch (err) {
        console.warn('[CanvasManager] dblclick handler error:', err);
      }
    };

    this.canvas.on('mouse:dblclick', handleTextDblClick);

    // Mobile: double-tap erkennen (mouse:dblclick feuert bei Touch nicht)
    let lastTapTime = 0;
    let lastTapTarget = null;
    this.canvas.on('mouse:down', (e) => {
      if (!('ontouchstart' in window)) return;
      const now = Date.now();
      const target = e && e.target;
      if (target && (now - lastTapTime) < 350 && target === lastTapTarget) {
        handleTextDblClick(e);
        lastTapTime = 0;
        lastTapTarget = null;
      } else {
        lastTapTime = now;
        lastTapTarget = target;
      }
    });

    this._setupCustomControls();

    return this.canvas;
  }

  _setupCustomControls() {
    const self = this;
    const CTRL_SIZE = 36;
    const CTRL_OFFSET = 0.5;

    // Helper: render icon with colored background — size passt sich an cornerSize an
    const renderIcon = (icon, bgColor = '#ffffff', iconColor = '#374151') => (ctx, left, top, styleOverride, fabricObject) => {
      const size = fabricObject.cornerSize || CTRL_SIZE;
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));

      // Background circle with shadow
      ctx.beginPath();
      ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
      ctx.fillStyle = bgColor;
      ctx.shadowColor = 'rgba(0,0,0,0.2)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 2;
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = bgColor === '#ffffff' ? '#d1d5db' : 'rgba(255,255,255,0.3)';
      ctx.lineWidth = Math.max(1, size / 36);
      ctx.stroke();

      // Icon — skaliert auf aktuelle size
      ctx.fillStyle = iconColor;
      ctx.strokeStyle = iconColor;
      ctx.lineWidth = Math.max(2, size / 18);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      icon(ctx, size);
      ctx.restore();
    };

    // --- Icon drawing functions (larger, bolder) ---
    const iconDuplicate = (ctx, s) => {
      const sz = s * 0.22;
      ctx.strokeRect(-sz - 1, -sz - 1, sz * 1.6, sz * 1.6);
      ctx.fillStyle = '#dbeafe';
      ctx.fillRect(sz * 0.4 - 1, sz * 0.4 - 1, sz * 1.6, sz * 1.6);
      ctx.strokeStyle = '#3b82f6';
      ctx.strokeRect(sz * 0.4 - 1, sz * 0.4 - 1, sz * 1.6, sz * 1.6);
    };

    const iconRotate = (ctx, s) => {
      const r = s * 0.28;
      ctx.beginPath();
      ctx.arc(0, 1, r, -Math.PI * 0.75, Math.PI * 0.5);
      ctx.stroke();
      const ax = r * Math.cos(Math.PI * 0.5);
      const ay = 1 + r * Math.sin(Math.PI * 0.5);
      ctx.beginPath();
      ctx.moveTo(ax - 4, ay - 3);
      ctx.lineTo(ax, ay);
      ctx.lineTo(ax + 5, ay - 2);
      ctx.stroke();
    };

    const iconDelete = (ctx, s) => {
      const w = s * 0.36, h = s * 0.40;
      ctx.strokeStyle = '#ef4444';
      // Lid
      ctx.beginPath();
      ctx.moveTo(-w / 2 - 2, -h / 2 + 3);
      ctx.lineTo(w / 2 + 2, -h / 2 + 3);
      ctx.stroke();
      // Handle
      ctx.beginPath();
      ctx.moveTo(-3, -h / 2 + 3);
      ctx.lineTo(-3, -h / 2);
      ctx.lineTo(3, -h / 2);
      ctx.lineTo(3, -h / 2 + 3);
      ctx.stroke();
      // Can body
      ctx.beginPath();
      ctx.moveTo(-w / 2, -h / 2 + 3);
      ctx.lineTo(-w / 2 + 1, h / 2);
      ctx.lineTo(w / 2 - 1, h / 2);
      ctx.lineTo(w / 2, -h / 2 + 3);
      ctx.stroke();
      // Lines inside
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(-2, -h / 2 + 6);
      ctx.lineTo(-2, h / 2 - 2);
      ctx.moveTo(2, -h / 2 + 6);
      ctx.lineTo(2, h / 2 - 2);
      ctx.stroke();
    };

    const iconScale = (ctx, s) => {
      const d = s * 0.26;
      ctx.beginPath();
      ctx.moveTo(-d, -d);
      ctx.lineTo(d, d);
      ctx.stroke();
      // Arrow heads (larger)
      ctx.beginPath();
      ctx.moveTo(d - 5, d);
      ctx.lineTo(d, d);
      ctx.lineTo(d, d - 5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-d + 5, -d);
      ctx.lineTo(-d, -d);
      ctx.lineTo(-d, -d + 5);
      ctx.stroke();
    };

    const iconResizeV = (ctx, s) => {
      const d = s * 0.28;
      ctx.beginPath();
      ctx.moveTo(0, -d);
      ctx.lineTo(0, d);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-4, -d + 5);
      ctx.lineTo(0, -d);
      ctx.lineTo(4, -d + 5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-4, d - 5);
      ctx.lineTo(0, d);
      ctx.lineTo(4, d - 5);
      ctx.stroke();
    };

    const iconResizeH = (ctx, s) => {
      const d = s * 0.28;
      ctx.beginPath();
      ctx.moveTo(-d, 0);
      ctx.lineTo(d, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-d + 5, -4);
      ctx.lineTo(-d, 0);
      ctx.lineTo(-d + 5, 4);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(d - 5, -4);
      ctx.lineTo(d, 0);
      ctx.lineTo(d - 5, 4);
      ctx.stroke();
    };

    // --- Custom control actions ---
    const duplicateAction = (eventData, transform) => {
      const obj = transform.target;
      // Gebogener Text: komplett neu bauen — sonst klont fabric nur Buchstaben ohne Curve-Properties
      if (obj._curvedText && obj._curvedType) {
        self._rebuildCurvedDuplicate(obj);
        return true;
      }
      obj.clone((cloned) => {
        cloned.set({ left: obj.left + 20, top: obj.top + 20 });
        self._copyCurvedProps(obj, cloned);
        self.canvas.add(cloned);
        self.canvas.discardActiveObject();
        self.canvas.setActiveObject(cloned);
        self.canvas.renderAll();
        eventBus.emit('object:selected', { target: [cloned] });
        eventBus.emit('layers:changed');
        eventBus.emit('canvas:changed');
      });
      return true;
    };

    const deleteAction = (eventData, transform) => {
      const obj = transform.target;
      self.canvas.remove(obj);
      self.canvas.renderAll();
      eventBus.emit('layers:changed');
      eventBus.emit('canvas:changed');
      return true;
    };

    // --- Apply to all object prototypes ---
    const customControls = {
      tl: new fabric.Control({
        x: -CTRL_OFFSET, y: -CTRL_OFFSET,
        cursorStyle: 'pointer',
        mouseUpHandler: duplicateAction,
        render: renderIcon(iconDuplicate, '#eff6ff', '#3b82f6'),
        actionName: 'duplicate',
        cornerSize: CTRL_SIZE,
      }),
      tr: new fabric.Control({
        x: CTRL_OFFSET, y: -CTRL_OFFSET,
        actionHandler: fabric.controlsUtils.rotationWithSnapping,
        cursorStyleHandler: fabric.controlsUtils.rotationStyleHandler,
        actionName: 'rotate',
        render: renderIcon(iconRotate, '#f0fdf4', '#22c55e'),
        cornerSize: CTRL_SIZE,
        withConnection: false,
      }),
      bl: new fabric.Control({
        x: -CTRL_OFFSET, y: CTRL_OFFSET,
        cursorStyle: 'pointer',
        mouseUpHandler: deleteAction,
        render: renderIcon(iconDelete, '#fef2f2', '#ef4444'),
        actionName: 'delete',
        cornerSize: CTRL_SIZE,
      }),
      br: new fabric.Control({
        x: CTRL_OFFSET, y: CTRL_OFFSET,
        actionHandler: fabric.controlsUtils.scalingEqually,
        cursorStyleHandler: fabric.controlsUtils.scaleCursorStyleHandler,
        actionName: 'scale',
        render: renderIcon(iconScale, '#ffffff', '#374151'),
        cornerSize: CTRL_SIZE,
      }),
      // Mittel-Anker: UNPROPORTIONAL skalieren (scaleY/scaleX)
      // Wichtig: NIE changeHeight/changeWidth verwenden — das ändert die source-width/height
      // und crop't Bilder statt sie zu skalieren. Wir wollen reines scaleX/scaleY.
      mt: new fabric.Control({
        x: 0, y: -CTRL_OFFSET,
        actionHandler: fabric.controlsUtils.scalingYOrSkewingX,
        cursorStyleHandler: fabric.controlsUtils.scaleSkewCursorStyleHandler,
        actionName: 'scaleY',
        render: renderIcon(iconResizeV),
        cornerSize: CTRL_SIZE,
      }),
      mb: new fabric.Control({
        x: 0, y: CTRL_OFFSET,
        actionHandler: fabric.controlsUtils.scalingYOrSkewingX,
        cursorStyleHandler: fabric.controlsUtils.scaleSkewCursorStyleHandler,
        actionName: 'scaleY',
        render: renderIcon(iconResizeV),
        cornerSize: CTRL_SIZE,
      }),
      ml: new fabric.Control({
        x: -CTRL_OFFSET, y: 0,
        actionHandler: fabric.controlsUtils.scalingXOrSkewingY,
        cursorStyleHandler: fabric.controlsUtils.scaleSkewCursorStyleHandler,
        actionName: 'scaleX',
        render: renderIcon(iconResizeH),
        cornerSize: CTRL_SIZE,
      }),
      mr: new fabric.Control({
        x: CTRL_OFFSET, y: 0,
        actionHandler: fabric.controlsUtils.scalingXOrSkewingY,
        cursorStyleHandler: fabric.controlsUtils.scaleSkewCursorStyleHandler,
        actionName: 'scaleX',
        render: renderIcon(iconResizeH),
        cornerSize: CTRL_SIZE,
      }),
    };

    // Remove the default mtr (rotation) control since we use tr for rotation
    delete customControls.mtr;

    // Apply to all object types
    fabric.Object.prototype.controls = customControls;
    fabric.Object.prototype.cornerSize = CTRL_SIZE;
    fabric.Object.prototype.cornerStyle = 'circle';
    fabric.Object.prototype.transparentCorners = false;
    fabric.Object.prototype.borderColor = '#3b82f6';
    fabric.Object.prototype.borderScaleFactor = 1.5;
    fabric.Object.prototype.padding = 8;
  }

  /**
   * Control-Größe an den Zoom anpassen, damit sie auf dem Bildschirm immer gleich groß erscheinen.
   * displayZoom = aktueller Anzeige-Zoom (relativ zur virtuellen Größe)
   */
  updateControlSizeForZoom(displayZoom) {
    if (!this.canvas || !displayZoom || displayZoom <= 0) return;
    const TARGET_SCREEN_SIZE = 36;
    const cssScale = displayZoom / (this.internalScale || 1);
    const newSize = Math.round(TARGET_SCREEN_SIZE / cssScale);
    const newPadding = Math.max(4, Math.round(10 / cssScale));
    const newBorderScale = Math.max(1, Math.round(2 / cssScale));
    const newTouchSize = newSize + Math.round(8 / cssScale);

    // Prototyp aktualisieren (für neue Objekte)
    fabric.Object.prototype.cornerSize = newSize;
    fabric.Object.prototype.touchCornerSize = newTouchSize;
    fabric.Object.prototype.padding = newPadding;
    fabric.Object.prototype.borderScaleFactor = newBorderScale;

    // Alle existierenden Objekte aktualisieren
    this.canvas.getObjects().forEach(obj => {
      obj.set({
        cornerSize: newSize,
        touchCornerSize: newTouchSize,
        padding: newPadding,
        borderScaleFactor: newBorderScale,
      });
      obj.setCoords();
    });
    this.canvas.renderAll();
  }

  _bindEvents() {
    this.canvas.on('selection:created', (e) => {
      eventBus.emit('object:selected', { target: e.selected });
    });
    this.canvas.on('selection:updated', (e) => {
      eventBus.emit('object:selected', { target: e.selected });
    });
    this.canvas.on('selection:cleared', () => {
      eventBus.emit('selection:cleared');
      eventBus.emit('toolbar:close-dialogs');
    });
    this.canvas.on('object:modified', (e) => {
      eventBus.emit('object:modified', { target: e.target });
      eventBus.emit('object:rotate-end');
      eventBus.emit('canvas:changed');
      // Immediate collision/bleed check after object is placed
      this._checkObjectWarnings(e.target);
    });
    // Corner-Handles (tl/tr/bl/br) erzwingen proportional, Mid-Sides bleiben einaxig
    this.canvas.on('object:scaling', (e) => {
      const t = e.transform;
      if (!t || !t.corner) return;
      if (t.corner === 'tl' || t.corner === 'tr' || t.corner === 'bl' || t.corner === 'br') {
        const target = e.target;
        const sx = target.scaleX || 1;
        const sy = target.scaleY || 1;
        const s = Math.max(Math.abs(sx), Math.abs(sy));
        target.set({
          scaleX: sx < 0 ? -s : s,
          scaleY: sy < 0 ? -s : s,
        });
      }
    });
    this.canvas.on('object:added', (e) => {
      const t = e.target;
      // Transient/overlay objects do NOT trigger history saves or layer refreshes
      const isTransient = t && (t.isSnapLine || t.isGuide || t.isGrid || t.isBleed || t.isHem || t.isHohlsaum || t.isGrommet);
      if (!t.isBleed && !t.isHem && !t.isHohlsaum && !t.isGrommet && !t.isGrid) {
        this._bringOverlaysToFront();
      }
      eventBus.emit('object:added', { target: t });
      if (!isTransient) eventBus.emit('canvas:changed');
    });
    this.canvas.on('object:removed', (e) => {
      const t = e.target;
      const isTransient = t && (t.isSnapLine || t.isGuide || t.isGrid || t.isBleed || t.isHem || t.isHohlsaum || t.isGrommet);
      eventBus.emit('object:removed', { target: t });
      if (!isTransient) eventBus.emit('canvas:changed');
    });

    // Click anywhere on canvas → close left toolbar dialogs + collage placeholder handling
    this.canvas.on('mouse:down', (opt) => {
      // Always emit stage:click so left toolbar popups close
      eventBus.emit('stage:click');

      const target = opt.target;
      if (target && (target.isCollagePlaceholder || target.isCollageLabel) && target.collageId) {
        const allObjs = this.canvas.getObjects();
        const placeholders = allObjs.filter(o => o.isCollagePlaceholder && o.collageId === target.collageId);
        if (placeholders.length > 0) {
          eventBus.emit('collage:placeholder-clicked', {
            collageId: target.collageId,
            placeholderCount: placeholders.length,
          });
        }
      }
    });

    // Rotation angle display
    this.canvas.on('object:rotating', (e) => {
      const angle = Math.round(e.target.angle % 360);
      eventBus.emit('object:rotating', { angle });
    });

    // Snap-to-center guidelines
    this._initSnapping();
  }

  _initSnapping() {
    this._snapLines = [];
    const BASE_SNAP_THRESHOLD = 18; // px tolerance at 100% zoom

    this.canvas.on('object:moving', (e) => {
      this._clearSnapLines();
      const obj = e.target;
      if (!obj || obj.isBleed || obj.isHem || obj.isGrid || obj.isGrommet || obj.isHohlsaum || obj.isSnapLine || obj.isGuide) return;

      // Scale threshold inversely with zoom so snapping works on mobile (small zoom = larger threshold)
      const currentZoom = this.canvas.getZoom() || 1;
      const SNAP_THRESHOLD = Math.max(BASE_SNAP_THRESHOLD, BASE_SNAP_THRESHOLD / currentZoom);

      const cw = this.vWidth;
      const ch = this.vHeight;

      const bound = obj.getBoundingRect(true, true);
      const objCX = bound.left + bound.width / 2;
      const objCY = bound.top + bound.height / 2;
      const objL = bound.left;
      const objR = bound.left + bound.width;
      const objT = bound.top;
      const objB = bound.top + bound.height;

      // Line style: bright magenta/pink for center, blue for edges
      const sw = Math.max(1.5, Math.min(cw, ch) / 800);
      const centerColor = '#ff2d7b';
      const edgeColor = '#3b82f6';

      const addVLine = (x, color) => {
        const line = new fabric.Line([x, 0, x, ch], {
          stroke: color, strokeWidth: sw, opacity: 0.85,
          selectable: false, evented: false, excludeFromExport: true, isSnapLine: true,
        });
        this.canvas.add(line);
        this._snapLines.push(line);
      };
      const addHLine = (y, color) => {
        const line = new fabric.Line([0, y, cw, y], {
          stroke: color, strokeWidth: sw, opacity: 0.85,
          selectable: false, evented: false, excludeFromExport: true, isSnapLine: true,
        });
        this.canvas.add(line);
        this._snapLines.push(line);
      };

      // Vertical snap targets: center, left edge, right edge + custom guides
      const vTargets = [
        { pos: cw / 2, type: 'center' },   // canvas center
        { pos: 0, type: 'edge' },           // left edge
        { pos: cw, type: 'edge' },          // right edge
      ];
      // Horizontal snap targets: center, top edge, bottom edge + custom guides
      const hTargets = [
        { pos: ch / 2, type: 'center' },   // canvas center
        { pos: 0, type: 'edge' },           // top edge
        { pos: ch, type: 'edge' },          // bottom edge
      ];
      // Add custom guidelines as snap targets
      for (const g of this._guides) {
        if (g.guideOrientation === 'vertical') {
          vTargets.push({ pos: g.guidePositionPx, type: 'guide' });
        } else {
          hTargets.push({ pos: g.guidePositionPx, type: 'guide' });
        }
      }

      // Snap object's center or edges to vertical targets
      const guideColor = '#2196F3';
      for (const t of vTargets) {
        const color = t.type === 'center' ? centerColor : t.type === 'guide' ? guideColor : edgeColor;
        // Object center → target
        if (Math.abs(objCX - t.pos) < SNAP_THRESHOLD) {
          obj.set('left', t.pos - (bound.width / 2) + (obj.left - bound.left));
          addVLine(t.pos, color);
          break;
        }
        // Object left edge → target
        if (Math.abs(objL - t.pos) < SNAP_THRESHOLD) {
          obj.set('left', t.pos + (obj.left - bound.left));
          addVLine(t.pos, color);
          break;
        }
        // Object right edge → target
        if (Math.abs(objR - t.pos) < SNAP_THRESHOLD) {
          obj.set('left', t.pos - bound.width + (obj.left - bound.left));
          addVLine(t.pos, color);
          break;
        }
      }

      // Snap object's center or edges to horizontal targets
      for (const t of hTargets) {
        const color = t.type === 'center' ? centerColor : t.type === 'guide' ? guideColor : edgeColor;
        if (Math.abs(objCY - t.pos) < SNAP_THRESHOLD) {
          obj.set('top', t.pos - (bound.height / 2) + (obj.top - bound.top));
          addHLine(t.pos, color);
          break;
        }
        if (Math.abs(objT - t.pos) < SNAP_THRESHOLD) {
          obj.set('top', t.pos + (obj.top - bound.top));
          addHLine(t.pos, color);
          break;
        }
        if (Math.abs(objB - t.pos) < SNAP_THRESHOLD) {
          obj.set('top', t.pos - bound.height + (obj.top - bound.top));
          addHLine(t.pos, color);
          break;
        }
      }

      // Also snap to OTHER user objects (align centers/edges)
      const others = this.canvas.getObjects().filter(o =>
        o !== obj && !o.isBleed && !o.isHem && !o.isGrid && !o.isGrommet && !o.isHohlsaum &&
        !o.isSnapLine && !o.isGuide && !o.isCollagePlaceholder && !o.isCollageLabel &&
        !o.isNumberOutline && o.selectable !== false
      );
      const alignColor = '#a855f7'; // purple for object-to-object alignment
      const updatedBound = obj.getBoundingRect(true, true);
      const uCX = updatedBound.left + updatedBound.width / 2;
      const uCY = updatedBound.top + updatedBound.height / 2;

      for (const other of others) {
        const ob = other.getBoundingRect(true, true);
        const oCX = ob.left + ob.width / 2;
        const oCY = ob.top + ob.height / 2;
        // Vertical center-to-center
        if (Math.abs(uCX - oCX) < SNAP_THRESHOLD && this._snapLines.filter(l => l._snapAxis === 'v').length === 0) {
          obj.set('left', oCX - (updatedBound.width / 2) + (obj.left - updatedBound.left));
          const line = new fabric.Line([oCX, 0, oCX, ch], {
            stroke: alignColor, strokeWidth: sw, opacity: 0.7,
            selectable: false, evented: false, excludeFromExport: true, isSnapLine: true,
          });
          line._snapAxis = 'v';
          this.canvas.add(line);
          this._snapLines.push(line);
        }
        // Horizontal center-to-center
        if (Math.abs(uCY - oCY) < SNAP_THRESHOLD && this._snapLines.filter(l => l._snapAxis === 'h').length === 0) {
          obj.set('top', oCY - (updatedBound.height / 2) + (obj.top - updatedBound.top));
          const line = new fabric.Line([0, oCY, cw, oCY], {
            stroke: alignColor, strokeWidth: sw, opacity: 0.7,
            selectable: false, evented: false, excludeFromExport: true, isSnapLine: true,
          });
          line._snapAxis = 'h';
          this.canvas.add(line);
          this._snapLines.push(line);
        }
      }

      obj.setCoords();

      // Debounced collision/bleed warning during drag (300ms)
      if (this._warningTimeout) clearTimeout(this._warningTimeout);
      this._warningTimeout = setTimeout(() => this._checkObjectWarnings(obj), 300);
    });

    this.canvas.on('object:modified', () => {
      this._clearSnapLines();
    });

    this.canvas.on('mouse:up', () => {
      setTimeout(() => this._clearSnapLines(), 400);
    });
  }

  _clearSnapLines() {
    if (this._snapLines.length === 0) return;
    this._snapLines.forEach((l) => this.canvas.remove(l));
    this._snapLines = [];
    this.canvas.renderAll();
  }

  // ── Custom Guidelines (user-draggable from rulers) ──────────────────────────

  /**
   * Add a custom guideline at a position in virtual canvas px.
   * @param {'horizontal'|'vertical'} orientation
   * @param {number} positionPx - position in virtual canvas pixels
   * @returns {fabric.Line} the created guide line
   */
  addGuide(orientation, positionPx) {
    const cw = this.vWidth;
    const ch = this.vHeight;
    const guideColor = '#2196F3';
    const sw = Math.max(1, Math.min(cw, ch) / 1200);
    let line;
    if (orientation === 'horizontal') {
      line = new fabric.Line([0, positionPx, cw, positionPx], {
        stroke: guideColor, strokeWidth: sw, strokeDashArray: [8, 4],
        opacity: 0.85, selectable: false, evented: true,
        excludeFromExport: true, isGuide: true, guideOrientation: 'horizontal',
        guidePositionPx: positionPx, hoverCursor: 'row-resize',
        hasControls: false, hasBorders: false, lockMovementX: true,
        perPixelTargetFind: false, padding: 6,
      });
    } else {
      line = new fabric.Line([positionPx, 0, positionPx, ch], {
        stroke: guideColor, strokeWidth: sw, strokeDashArray: [8, 4],
        opacity: 0.85, selectable: false, evented: true,
        excludeFromExport: true, isGuide: true, guideOrientation: 'vertical',
        guidePositionPx: positionPx, hoverCursor: 'col-resize',
        hasControls: false, hasBorders: false, lockMovementY: true,
        perPixelTargetFind: false, padding: 6,
      });
    }
    this.canvas.add(line);
    this._guides.push(line);
    this.canvas.renderAll();
    eventBus.emit('guides:changed', this.getGuides());
    return line;
  }

  /**
   * Remove a specific guideline from the canvas.
   */
  removeGuide(guide) {
    this.canvas.remove(guide);
    this._guides = this._guides.filter(g => g !== guide);
    this.canvas.renderAll();
    eventBus.emit('guides:changed', this.getGuides());
  }

  /**
   * Get all current guidelines.
   */
  getGuides() {
    return this._guides.map(g => ({
      orientation: g.guideOrientation,
      positionPx: g.guidePositionPx,
      positionCm: Math.round((g.guidePositionPx / PX_PER_CM) * 100) / 100,
    }));
  }

  /**
   * Remove all custom guidelines.
   */
  clearGuides() {
    this._guides.forEach(g => this.canvas.remove(g));
    this._guides = [];
    this.canvas.renderAll();
    eventBus.emit('guides:changed', []);
  }

  /**
   * Update guide dimensions when canvas size changes.
   */
  _updateGuides() {
    const cw = this.vWidth;
    const ch = this.vHeight;
    this._guides.forEach(g => {
      if (g.guideOrientation === 'horizontal') {
        g.set({ x1: 0, y1: g.guidePositionPx, x2: cw, y2: g.guidePositionPx });
      } else {
        g.set({ x1: g.guidePositionPx, y1: 0, x2: g.guidePositionPx, y2: ch });
      }
      g.setCoords();
    });
  }

  /**
   * Check if a fabric object at mouse position is a guideline, for right-click/double-click delete.
   */
  getGuideAtPoint(canvasPx, canvasPy) {
    // Search guides for one near the point
    const threshold = 8 / (this.canvas.getZoom() || 1);
    for (const g of this._guides) {
      if (g.guideOrientation === 'horizontal') {
        if (Math.abs(canvasPy - g.guidePositionPx) < threshold) return g;
      } else {
        if (Math.abs(canvasPx - g.guidePositionPx) < threshold) return g;
      }
    }
    return null;
  }

  /**
   * Check if a user object collides with grommets or extends beyond bleed lines.
   * Shows visual warning (temporary red border), toast, and debounced speech.
   */
  _checkObjectWarnings(obj) {
    if (!obj) return;
    // Skip overlay/internal objects
    if (obj.isBleed || obj.isHem || obj.isGrommet || obj.isSnapLine || obj.isGrid || obj.isHohlsaum || obj.isGuide || obj.isCollagePlaceholder || obj.isCollageLabel) return;

    const bound = obj.getBoundingRect(true, true);
    const isImage = obj.type === 'image';
    const isText = obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text';
    let hasGrommetCollision = false;
    let hasBleedOverflow = false;

    // 1. Grommet collision — only for text objects (not images)
    if (!isImage && this.grommetsObjects && this.grommetsObjects.length > 0) {
      for (const grommet of this.grommetsObjects) {
        const gBound = grommet.getBoundingRect(true, true);
        if (
          bound.left < gBound.left + gBound.width &&
          bound.left + bound.width > gBound.left &&
          bound.top < gBound.top + gBound.height &&
          bound.top + bound.height > gBound.top
        ) {
          hasGrommetCollision = true;
          break;
        }
      }
    }

    // 2. Bleed overflow — warn only if object goes BEYOND the bleed (outside canvas)
    //    The bleed area itself MUST be filled for a clean cut — no warning there.
    if (this.bleedCm > 0) {
      const cw = this.vWidth;
      const ch = this.vHeight;
      if (
        bound.left < 0 ||
        bound.top < 0 ||
        bound.left + bound.width > cw ||
        bound.top + bound.height > ch
      ) {
        hasBleedOverflow = true;
      }
    }

    if (!hasGrommetCollision && !hasBleedOverflow) return;

    // 3. Visual warning — temporary colored border
    const origStroke = obj._origWarningStroke !== undefined ? obj._origWarningStroke : (obj.stroke || null);
    const origStrokeWidth = obj._origWarningStrokeWidth !== undefined ? obj._origWarningStrokeWidth : (obj.strokeWidth || 0);
    if (obj._origWarningStroke === undefined) {
      obj._origWarningStroke = obj.stroke || null;
      obj._origWarningStrokeWidth = obj.strokeWidth || 0;
    }

    obj.set({
      stroke: hasGrommetCollision ? '#ef4444' : '#f59e0b',
      strokeWidth: 2,
    });
    this.canvas.renderAll();

    const resetTimer = setTimeout(() => {
      if (obj.canvas) {
        obj.set({ stroke: origStroke, strokeWidth: origStrokeWidth });
        delete obj._origWarningStroke;
        delete obj._origWarningStrokeWidth;
        this.canvas.renderAll();
      }
    }, 3000);
    this._warningResetTimers.push(resetTimer);

    // 4. Toast notification
    if (hasGrommetCollision) {
      eventBus.emit('warning', { message: 'Achtung: Dein Text überlappt mit einer Öse und könnte beim Stanzen beschädigt werden.' });
    }
    if (hasBleedOverflow) {
      const label = isImage ? 'Bild' : 'Element';
      eventBus.emit('warning', { message: 'Hinweis: Dein ' + label + ' ragt über den Druckbereich hinaus. Der überstehende Teil wird abgeschnitten.' });
    }

    // 5. Speech warning (debounced — max once every 5 seconds)
    const now = Date.now();
    // Warnungen werden nicht vorgelesen — visuelle Hinweise reichen
  }

  setDimensions(widthCm, heightCm, fitContent = false) {
    const oldVW = this.vWidth || (this.widthCm * PX_PER_CM);
    const oldVH = this.vHeight || (this.heightCm * PX_PER_CM);

    this.widthCm = widthCm;
    this.heightCm = heightCm;
    this.internalScale = this._calcInternalScale(widthCm, heightCm);
    const virtualW = Math.round(widthCm * PX_PER_CM);
    const virtualH = Math.round(heightCm * PX_PER_CM);
    const actualW = Math.round(virtualW * this.internalScale);
    const actualH = Math.round(virtualH * this.internalScale);

    // Scale all content proportionally when dimensions change and content exists
    if (fitContent && oldVW > 0 && oldVH > 0) {
      const scaleX = virtualW / oldVW;
      const scaleY = virtualH / oldVH;

      this.canvas.getObjects().forEach(obj => {
        // Skip helper objects (bleed lines, hem lines, grommets)
        if (obj.customType === 'bleedLine' || obj.customType === 'hemLine' ||
            obj.customType === 'grommet' || obj.customType === 'hohlsaum' ||
            obj.isHelper) return;

        // Scale position
        obj.set({
          left: (obj.left || 0) * scaleX,
          top: (obj.top || 0) * scaleY,
          scaleX: (obj.scaleX || 1) * scaleX,
          scaleY: (obj.scaleY || 1) * scaleY,
        });
        obj.setCoords();
      });

      // Scale background image
      const bgImg = this.canvas.backgroundImage;
      if (bgImg) {
        bgImg.set({
          scaleX: virtualW / bgImg.width,
          scaleY: virtualH / bgImg.height,
        });
      }
    }

    this.canvas.setDimensions({ width: actualW, height: actualH });
    this.canvas.setZoom(this.internalScale);
    this._removeBleedLines();
    this.drawBleedLines(this.bleedCm);
    this.drawHemLines(this.hemPerSide || this.hemCm);
    this.drawHohlsaum(this.hohlsaumConfig);
    this.drawGrommets(this.grommetsConfig, this.hemPerSide || this.hemCm);
    this._updateGuides();
    this.canvas.renderAll();
    eventBus.emit('canvas:resized', { widthCm, heightCm });
  }

  getDimensions() {
    return { widthCm: this.widthCm, heightCm: this.heightCm };
  }

  // --- Drawing Tools ---

  // Returns a scaling factor so objects look ~15% of the shortest canvas side
  _objScale() {
    const w = this.vWidth;
    const h = this.vHeight;
    const shortest = Math.min(w, h);
    return shortest / 600; // baseline: 600px canvas → scale 1
  }

  getObjScale() {
    return this._objScale();
  }

  _centerLeft(objW) {
    return (this.vWidth - objW) / 2;
  }

  _centerTop(objH) {
    return (this.vHeight - objH) / 2;
  }

  addRectangle(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(150 * s);
    const h = options.height || Math.round(100 * s);
    const rect = new fabric.Rect({
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      width: w,
      height: h,
      fill: options.fill || '#3b82f6',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      rx: options.rx || 0,
      ry: options.ry || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Rechteck',
    });
    this.canvas.add(rect);
    this.canvas.setActiveObject(rect);
    this.canvas.renderAll();
    return rect;
  }

  addCircle(options = {}) {
    const s = this._objScale();
    const r = options.radius || Math.round(75 * s);
    const circle = new fabric.Circle({
      left: options.left ?? this._centerLeft(r * 2),
      top: options.top ?? this._centerTop(r * 2),
      radius: r,
      fill: options.fill || '#ef4444',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Kreis',
    });
    this.canvas.add(circle);
    this.canvas.setActiveObject(circle);
    this.canvas.renderAll();
    return circle;
  }

  addLine(options = {}) {
    const s = this._objScale();
    const cw = this.vWidth;
    const ch = this.vHeight;
    const len = Math.round(200 * s);
    const x1 = options.x1 ?? (cw / 2 - len / 2);
    const y1 = options.y1 ?? (ch / 2);
    const x2 = options.x2 ?? (cw / 2 + len / 2);
    const y2 = options.y2 ?? (ch / 2);
    const line = new fabric.Line([x1, y1, x2, y2], {
      stroke: options.stroke || '#000000',
      strokeWidth: options.strokeWidth || Math.max(2, Math.round(2 * s)),
      opacity: options.opacity ?? 1,
      customName: options.name || 'Linie',
    });
    this.canvas.add(line);
    this.canvas.setActiveObject(line);
    this.canvas.renderAll();
    return line;
  }

  addPolygon(points, options = {}) {
    const s = this._objScale();
    const defaultPoints = points || [
      { x: Math.round(100 * s), y: 0 },
      { x: Math.round(200 * s), y: Math.round(75 * s) },
      { x: Math.round(150 * s), y: Math.round(175 * s) },
      { x: Math.round(50 * s), y: Math.round(175 * s) },
      { x: 0, y: Math.round(75 * s) },
    ];
    const polygon = new fabric.Polygon(defaultPoints, {
      left: options.left ?? this._centerLeft(Math.round(200 * s)),
      top: options.top ?? this._centerTop(Math.round(175 * s)),
      fill: options.fill || '#f59e0b',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Polygon',
    });
    this.canvas.add(polygon);
    this.canvas.setActiveObject(polygon);
    this.canvas.renderAll();
    return polygon;
  }

  addTriangle(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(150 * s);
    const h = options.height || Math.round(130 * s);
    const tri = new fabric.Triangle({
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      width: w,
      height: h,
      fill: options.fill || '#8b5cf6',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Dreieck',
    });
    this.canvas.add(tri);
    this.canvas.setActiveObject(tri);
    this.canvas.renderAll();
    return tri;
  }

  addEllipse(options = {}) {
    const s = this._objScale();
    const rx = options.rx || Math.round(100 * s);
    const ry = options.ry || Math.round(60 * s);
    const ellipse = new fabric.Ellipse({
      left: options.left ?? this._centerLeft(rx * 2),
      top: options.top ?? this._centerTop(ry * 2),
      rx: rx,
      ry: ry,
      fill: options.fill || '#ec4899',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Ellipse',
    });
    this.canvas.add(ellipse);
    this.canvas.setActiveObject(ellipse);
    this.canvas.renderAll();
    return ellipse;
  }

  addRoundedRect(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(160 * s);
    const h = options.height || Math.round(100 * s);
    const rect = new fabric.Rect({
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      width: w,
      height: h,
      rx: options.rx || Math.round(20 * s),
      ry: options.ry || Math.round(20 * s),
      fill: options.fill || '#06b6d4',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Abgerundetes Rechteck',
    });
    this.canvas.add(rect);
    this.canvas.setActiveObject(rect);
    this.canvas.renderAll();
    return rect;
  }

  addStar(options = {}) {
    const s = this._objScale();
    const outerR = options.outerR || Math.round(70 * s);
    const innerR = options.innerR || Math.round(30 * s);
    const points = this._createStarPoints(options.points || 5, outerR, innerR);
    const star = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(outerR * 2),
      top: options.top ?? this._centerTop(outerR * 2),
      fill: options.fill || '#eab308',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Stern',
    });
    this.canvas.add(star);
    this.canvas.setActiveObject(star);
    this.canvas.renderAll();
    return star;
  }

  // Explosionsstern mit unregelmäßigen Spitzen-Längen — Comic/Knall-Look
  _addBurstStar(numPoints, innerRatio, jitter, fallbackName, options = {}) {
    const s = this._objScale();
    const outerR = options.outerR || Math.round(80 * s);
    const innerR = options.innerR || Math.round(outerR * innerRatio);
    const points = this._createBurstPoints(numPoints, outerR, innerR, options.jitter ?? jitter);
    const star = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(outerR * 2),
      top: options.top ?? this._centerTop(outerR * 2),
      fill: options.fill || '#eab308',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || fallbackName,
    });
    this.canvas.add(star);
    this.canvas.setActiveObject(star);
    this.canvas.renderAll();
    return star;
  }

  addStarBurst12(options = {}) {
    return this._addBurstStar(12, 0.50, 0.40, 'Explosion 12', options);
  }

  addStarBurst16(options = {}) {
    return this._addBurstStar(16, 0.55, 0.35, 'Explosion 16', options);
  }

  addStarBurst24(options = {}) {
    return this._addBurstStar(24, 0.60, 0.28, 'Explosion 24', options);
  }

  addHexagon(options = {}) {
    const s = this._objScale();
    const r = options.radius || Math.round(70 * s);
    const points = this._createRegularPolygonPoints(6, r);
    const hex = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(r * 2),
      top: options.top ?? this._centerTop(r * 2),
      fill: options.fill || '#14b8a6',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Hexagon',
    });
    this.canvas.add(hex);
    this.canvas.setActiveObject(hex);
    this.canvas.renderAll();
    return hex;
  }

  addOctagon(options = {}) {
    const s = this._objScale();
    const r = options.radius || Math.round(70 * s);
    const points = this._createRegularPolygonPoints(8, r);
    const oct = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(r * 2),
      top: options.top ?? this._centerTop(r * 2),
      fill: options.fill || '#f97316',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Achteck',
    });
    this.canvas.add(oct);
    this.canvas.setActiveObject(oct);
    this.canvas.renderAll();
    return oct;
  }

  addDiamond(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(120 * s);
    const h = options.height || Math.round(160 * s);
    const points = [
      { x: w / 2, y: 0 },
      { x: w, y: h / 2 },
      { x: w / 2, y: h },
      { x: 0, y: h / 2 },
    ];
    const diamond = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#a855f7',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Raute',
    });
    this.canvas.add(diamond);
    this.canvas.setActiveObject(diamond);
    this.canvas.renderAll();
    return diamond;
  }

  addArrowRight(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(180 * s);
    const h = options.height || Math.round(100 * s);
    const points = [
      { x: 0, y: h * 0.25 },
      { x: w * 0.65, y: h * 0.25 },
      { x: w * 0.65, y: 0 },
      { x: w, y: h * 0.5 },
      { x: w * 0.65, y: h },
      { x: w * 0.65, y: h * 0.75 },
      { x: 0, y: h * 0.75 },
    ];
    const arrow = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#3b82f6',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Pfeil',
    });
    this.canvas.add(arrow);
    this.canvas.setActiveObject(arrow);
    this.canvas.renderAll();
    return arrow;
  }

  addHeart(options = {}) {
    const s = this._objScale();
    const path = 'M 140 20 C 73 20 20 74 20 140 C 20 275 156 310 260 400 C 364 310 500 275 500 140 C 500 74 447 20 380 20 C 324 20 280 55 260 95 C 240 55 196 20 140 20 Z';
    const heartScale = options.scale || 0.3 * s;
    const heartW = 500 * heartScale;
    const heartH = 400 * heartScale;
    const heart = new fabric.Path(path, {
      left: options.left ?? this._centerLeft(heartW),
      top: options.top ?? this._centerTop(heartH),
      fill: options.fill || '#ef4444',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      scaleX: heartScale,
      scaleY: heartScale,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Herz',
    });
    this.canvas.add(heart);
    this.canvas.setActiveObject(heart);
    this.canvas.renderAll();
    return heart;
  }

  addCross(options = {}) {
    const sc = this._objScale();
    const sz = options.size || Math.round(140 * sc);
    const t = sz * 0.33;
    const points = [
      { x: t, y: 0 }, { x: sz - t, y: 0 },
      { x: sz - t, y: t }, { x: sz, y: t },
      { x: sz, y: sz - t }, { x: sz - t, y: sz - t },
      { x: sz - t, y: sz }, { x: t, y: sz },
      { x: t, y: sz - t }, { x: 0, y: sz - t },
      { x: 0, y: t }, { x: t, y: t },
    ];
    const cross = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(sz),
      top: options.top ?? this._centerTop(sz),
      fill: options.fill || '#22c55e',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Kreuz',
    });
    this.canvas.add(cross);
    this.canvas.setActiveObject(cross);
    this.canvas.renderAll();
    return cross;
  }

  addParallelogram(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(180 * s);
    const h = options.height || Math.round(100 * s);
    const skew = options.skew || Math.round(30 * s);
    const points = [
      { x: skew, y: 0 },
      { x: w, y: 0 },
      { x: w - skew, y: h },
      { x: 0, y: h },
    ];
    const para = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#64748b',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Parallelogramm',
    });
    this.canvas.add(para);
    this.canvas.setActiveObject(para);
    this.canvas.renderAll();
    return para;
  }

  addTrapezoid(options = {}) {
    const s = this._objScale();
    const w = options.width || Math.round(180 * s);
    const h = options.height || Math.round(100 * s);
    const indent = options.indent || Math.round(30 * s);
    const points = [
      { x: indent, y: 0 },
      { x: w - indent, y: 0 },
      { x: w, y: h },
      { x: 0, y: h },
    ];
    const trap = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#d946ef',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Trapez',
    });
    this.canvas.add(trap);
    this.canvas.setActiveObject(trap);
    this.canvas.renderAll();
    return trap;
  }

  addRing(options = {}) {
    const s = this._objScale();
    const outerR = options.outerR || Math.round(70 * s);
    const innerR = options.innerR || Math.round(45 * s);
    const outer = new fabric.Circle({
      radius: outerR,
      fill: '',
      stroke: options.fill || '#f59e0b',
      strokeWidth: outerR - innerR,
      left: options.left ?? this._centerLeft(outerR * 2),
      top: options.top ?? this._centerTop(outerR * 2),
      opacity: options.opacity ?? 1,
      customName: options.name || 'Ring',
    });
    this.canvas.add(outer);
    this.canvas.setActiveObject(outer);
    this.canvas.renderAll();
    return outer;
  }

  addBubble(options = {}) {
    const s = this._objScale();
    const path = 'M 20 2 C 8.954 2 0 8.954 0 20 C 0 31.046 8.954 38 20 38 L 14 46 L 22 38 C 33.046 38 42 31.046 42 20 C 42 8.954 33.046 2 22 2 Z';
    const bubbleScale = options.scale || 3 * s;
    const bubbleW = 42 * bubbleScale;
    const bubbleH = 46 * bubbleScale;
    const bubble = new fabric.Path(path, {
      left: options.left ?? this._centerLeft(bubbleW),
      top: options.top ?? this._centerTop(bubbleH),
      fill: options.fill || '#bfdbfe',
      stroke: options.stroke || '#3b82f6',
      strokeWidth: options.strokeWidth || 2,
      scaleX: bubbleScale,
      scaleY: bubbleScale,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Sprechblase',
    });
    this.canvas.add(bubble);
    this.canvas.setActiveObject(bubble);
    this.canvas.renderAll();
    return bubble;
  }

  addBadge(options = {}) {
    const s = this._objScale();
    const outerR = options.outerR || Math.round(70 * s);
    const innerR = options.innerR || Math.round(55 * s);
    const points = this._createStarPoints(12, outerR, innerR);
    const badge = new fabric.Polygon(points, {
      left: options.left ?? this._centerLeft(outerR * 2),
      top: options.top ?? this._centerTop(outerR * 2),
      fill: options.fill || '#dc2626',
      stroke: options.stroke || '',
      strokeWidth: options.strokeWidth || 0,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Badge',
    });
    this.canvas.add(badge);
    this.canvas.setActiveObject(badge);
    this.canvas.renderAll();
    return badge;
  }

  // --- Ribbon / Banner Shapes ---

  addRibbonFlat(options = {}) {
    const s = this._objScale();
    const w = Math.round((options.width || 400) * s);
    const h = Math.round((options.height || 80) * s);
    const fold = Math.round(h * 0.35);
    const tail = Math.round(w * 0.08);
    // Main ribbon body with folded ends
    const path = `M ${tail} 0 L ${w - tail} 0 L ${w} ${h / 2} L ${w - tail} ${h} L ${tail} ${h} L 0 ${h / 2} Z`;
    const ribbon = new fabric.Path(path, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#dc2626',
      stroke: options.stroke || '#991b1b',
      strokeWidth: Math.max(1, Math.round(1.5 * s)),
      customName: 'Ribbon',
    });
    this.canvas.add(ribbon);
    this.canvas.setActiveObject(ribbon);
    this.canvas.renderAll();
    return ribbon;
  }

  addRibbonCurved(options = {}) {
    const s = this._objScale();
    const w = Math.round((options.width || 450) * s);
    const h = Math.round((options.height || 90) * s);
    const t = Math.round(w * 0.1); // tail width
    const f = Math.round(h * 0.3); // fold depth
    const c = Math.round(h * 0.2); // curve amount
    const path = [
      `M 0 ${f}`,
      `L ${t} 0`,
      `L ${t} ${f}`,
      `Q ${w / 2} ${f + c} ${w - t} ${f}`,
      `L ${w - t} 0`,
      `L ${w} ${f}`,
      `L ${w - t} ${f + f}`,
      `L ${w - t} ${h - f}`,
      `Q ${w / 2} ${h - f - c} ${t} ${h - f}`,
      `L ${t} ${h - f + f}`,
      `Z`
    ].join(' ');
    const ribbon = new fabric.Path(path, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#dc2626',
      stroke: options.stroke || '#991b1b',
      strokeWidth: Math.max(1, Math.round(1.5 * s)),
      customName: 'Banner',
    });
    this.canvas.add(ribbon);
    this.canvas.setActiveObject(ribbon);
    this.canvas.renderAll();
    return ribbon;
  }

  addRibbonWave(options = {}) {
    const s = this._objScale();
    const w = Math.round((options.width || 500) * s);
    const h = Math.round((options.height || 70) * s);
    const t = Math.round(w * 0.06);
    const c = Math.round(h * 0.25);
    const path = [
      `M 0 ${h * 0.5}`,
      `L ${t} 0`,
      `Q ${w * 0.25} ${c} ${w * 0.5} 0`,
      `Q ${w * 0.75} ${-c} ${w - t} 0`,
      `L ${w} ${h * 0.5}`,
      `L ${w - t} ${h}`,
      `Q ${w * 0.75} ${h + c} ${w * 0.5} ${h}`,
      `Q ${w * 0.25} ${h - c} ${t} ${h}`,
      `Z`
    ].join(' ');
    const ribbon = new fabric.Path(path, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#b91c1c',
      stroke: options.stroke || '#7f1d1d',
      strokeWidth: Math.max(1, Math.round(1.5 * s)),
      customName: 'Welle-Banner',
    });
    this.canvas.add(ribbon);
    this.canvas.setActiveObject(ribbon);
    this.canvas.renderAll();
    return ribbon;
  }

  addRibbonScroll(options = {}) {
    const s = this._objScale();
    const w = Math.round((options.width || 420) * s);
    const h = Math.round((options.height || 80) * s);
    const t = Math.round(w * 0.12);
    const path = [
      `M ${t} 0`,
      `L ${w - t} 0`,
      `L ${w - t} ${-h * 0.15}`,
      `L ${w} ${-h * 0.15}`,
      `L ${w - t * 0.7} ${h * 0.35}`,
      `L ${w - t} ${h * 0.15}`,
      `L ${w - t} ${h}`,
      `L ${t} ${h}`,
      `L ${t} ${h * 0.85 + h * 0.15}`,
      `L 0 ${h + h * 0.15}`,
      `L ${t * 0.7} ${h * 0.65}`,
      `L ${t} ${h * 0.85}`,
      `Z`
    ].join(' ');
    const ribbon = new fabric.Path(path, {
      left: options.left ?? this._centerLeft(w),
      top: options.top ?? this._centerTop(h),
      fill: options.fill || '#dc2626',
      stroke: options.stroke || '#991b1b',
      strokeWidth: Math.max(1, Math.round(1.5 * s)),
      customName: 'Scroll-Banner',
    });
    this.canvas.add(ribbon);
    this.canvas.setActiveObject(ribbon);
    this.canvas.renderAll();
    return ribbon;
  }

  // --- Shape Helper Methods ---

  _createStarPoints(numPoints, outerRadius, innerRadius) {
    const points = [];
    const step = Math.PI / numPoints;
    for (let i = 0; i < numPoints * 2; i++) {
      const r = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = i * step - Math.PI / 2;
      points.push({
        x: outerRadius + r * Math.cos(angle),
        y: outerRadius + r * Math.sin(angle),
      });
    }
    return points;
  }

  // Explosionsstern mit zufälliger Spitzen-Länge — Comic-/Knall-Look.
  // jitterRange definiert den Bereich, in dem die Außen-Radien zufällig variieren (0..1 vom max-R).
  _createBurstPoints(numPoints, outerRadius, innerRadius, jitterRange = 0.35) {
    const points = [];
    const step = Math.PI / numPoints;
    // Erste Außen-Spitze immer am Max (für visuelles "Top"), Innen-Radien konstant.
    const minOuter = Math.round(outerRadius * (1 - jitterRange));
    for (let i = 0; i < numPoints * 2; i++) {
      let r;
      if (i % 2 === 0) {
        // Outer: per-Spike zufällig zwischen minOuter und outerRadius
        // Erste & letzte gegenüberliegende Spitze normal, dazwischen variieren
        r = i === 0 ? outerRadius : minOuter + Math.random() * (outerRadius - minOuter);
      } else {
        // Inner: leichte Variation für organischen Look (~5%)
        const innerJitter = innerRadius * 0.06 * (Math.random() - 0.5);
        r = innerRadius + innerJitter;
      }
      const angle = i * step - Math.PI / 2;
      points.push({
        x: outerRadius + r * Math.cos(angle),
        y: outerRadius + r * Math.sin(angle),
      });
    }
    return points;
  }

  _createRegularPolygonPoints(sides, radius) {
    const points = [];
    for (let i = 0; i < sides; i++) {
      const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
      points.push({
        x: radius + radius * Math.cos(angle),
        y: radius + radius * Math.sin(angle),
      });
    }
    return points;
  }

  addText(text = 'Text eingeben', options = {}) {
    const s = this._objScale();
    const fontSize = options.fontSize || Math.max(12, Math.round(24 * s));
    const textObj = new fabric.IText(text, {
      left: options.left ?? this._centerLeft(fontSize * text.length * 0.5),
      top: options.top ?? this._centerTop(fontSize),
      fontFamily: options.fontFamily || 'Inter',
      fontSize: fontSize,
      fill: options.fill || '#000000',
      fontWeight: options.fontWeight || 'normal',
      fontStyle: options.fontStyle || 'normal',
      textAlign: options.textAlign || 'left',
      underline: options.underline || false,
      linethrough: options.linethrough || false,
      opacity: options.opacity ?? 1,
      customName: options.name || 'Text',
      editable: false, // Disable inline editing (CSS zoom conflict)
      paintFirst: 'stroke',
      strokeLineJoin: 'round',
      strokeLineCap: 'round',
    });
    this.canvas.add(textObj);
    this.canvas.setActiveObject(textObj);
    this.canvas.renderAll();
    return textObj;
  }

  /**
   * 3D-Text mit einem bestimmten Preset-Effekt hinzufügen.
   * Wenn ein Text ausgewählt ist, wird er durch den 3D-Text ersetzt.
   */
  add3DText(presetId, text = 'Dein Text', fontSize = 72) {
    const preset = TEXT_3D_PRESETS.find(p => p.id === presetId);
    if (!preset) return null;
    const scale = this.getObjScale ? this.getObjScale() : 1;

    // Ausgewählten Text übernehmen und entfernen
    const selected = this.canvas.getActiveObject();
    let useText = text;
    let useFontSize = fontSize;
    let useLeft = null;
    let useTop = null;
    if (selected && ['i-text', 'text', 'textbox', 'extrusion3DIText', 'outlineStackIText', 'neonIText', 'retroIText'].includes(selected.type)) {
      useText = selected.text || text;
      useFontSize = selected.fontSize ? Math.round(selected.fontSize / scale) : fontSize;
      useLeft = selected.left;
      useTop = selected.top;
      this.canvas.remove(selected);
    }

    const obj = preset.create(useText, useFontSize, scale);
    obj.set({
      left: useLeft ?? this._centerLeft(obj.width || useFontSize * useText.length * 0.5),
      top: useTop ?? this._centerTop(obj.height || useFontSize),
    });
    this.canvas.add(obj);
    this.canvas.setActiveObject(obj);
    this.canvas.renderAll();
    eventBus.emit('object:selected', { target: [obj] });
    eventBus.emit('layers:changed');
    eventBus.emit('canvas:changed');
    return obj;
  }

  addImage(url, options = {}) {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        url,
        (img) => {
          if (!img || !img.width) {
            reject(new Error('Bild konnte nicht geladen werden'));
            return;
          }
          const s = this._objScale();

          // Scale to fit ~25% of shortest canvas side
          const maxDim = options.maxWidth || Math.round(300 * s);
          const ratio = Math.min(maxDim / img.width, maxDim / img.height);
          const scaleX = options.scaleX || ratio;
          const scaleY = options.scaleY || ratio;
          const imgW = img.width * scaleX;
          const imgH = img.height * scaleY;

          img.set({
            left: options.left ?? this._centerLeft(imgW),
            top: options.top ?? this._centerTop(imgH),
            scaleX: scaleX,
            scaleY: scaleY,
            angle: options.angle || 0,
            opacity: options.opacity ?? 1,
            customName: options.name || 'Bild',
          });

          this.canvas.add(img);
          this.canvas.setActiveObject(img);
          this.canvas.renderAll();
          resolve(img);
        },
        { crossOrigin: 'anonymous' }
      );
    });
  }

  addSVG(url, options = {}) {
    return new Promise((resolve, reject) => {
      fabric.loadSVGFromURL(url, (objects, svgOptions) => {
        if (!objects || objects.length === 0) {
          reject(new Error('SVG konnte nicht geladen werden'));
          return;
        }
        const group = fabric.util.groupSVGElements(objects, svgOptions);
        const s = this._objScale();
        const svgScale = options.scaleX || s;
        const svgW = (group.width || 100) * svgScale;
        const svgH = (group.height || 100) * svgScale;
        group.set({
          left: options.left ?? this._centerLeft(svgW),
          top: options.top ?? this._centerTop(svgH),
          scaleX: svgScale,
          scaleY: options.scaleY || svgScale,
          opacity: options.opacity ?? 1,
          customName: options.name || 'Clipart',
        });
        this.canvas.add(group);
        this.canvas.setActiveObject(group);
        this.canvas.renderAll();
        resolve(group);
      });
    });
  }

  async addQRCode(url, options = {}) {
    try {
      const s = this._objScale();
      const qrSize = options.size || Math.round(200 * s);
      const dataUrl = await QRCode.toDataURL(url, {
        width: qrSize,
        margin: 1,
        color: { dark: options.color || '#000000', light: '#ffffff' },
      });
      const img = await this.addImage(dataUrl, {
        name: 'QR-Code',
      });
      return img;
    } catch (err) {
      console.error('QR-Code Fehler:', err);
      throw err;
    }
  }

  // --- Object Actions ---

  deleteSelected() {
    const active = this.canvas.getActiveObjects();
    if (active.length === 0) return;
    active.forEach((obj) => this.canvas.remove(obj));
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
  }

  duplicateSelected() {
    const active = this.canvas.getActiveObject();
    if (!active) return;

    // Gebogener Text: KOMPLETT NEU bauen mit den gespeicherten Werten,
    // damit der Klon vollständig editierbar ist (statt fabric.clone der Buchstaben-Group).
    if (active._curvedText && active._curvedType) {
      this._rebuildCurvedDuplicate(active);
      return;
    }

    active.clone((cloned) => {
      cloned.set({ left: cloned.left + 20, top: cloned.top + 20 });
      this._copyCurvedProps(active, cloned);
      if (cloned.type === 'activeSelection') {
        cloned.canvas = this.canvas;
        cloned.forEachObject((obj) => this.canvas.add(obj));
        cloned.setCoords();
      } else {
        this.canvas.add(cloned);
      }
      this.canvas.discardActiveObject();
      this.canvas.setActiveObject(cloned);
      this.canvas.renderAll();
      eventBus.emit('object:selected', { target: [cloned] });
      eventBus.emit('layers:changed');
      eventBus.emit('canvas:changed');
    });
  }

  _rebuildCurvedDuplicate(src) {
    const tempText = new fabric.Text(src._curvedText, {
      fontSize: src._curvedFontSize || 40,
      fontFamily: src._curvedFontFamily || 'Arial',
      fontWeight: src._curvedFontWeight || 'normal',
      fill: src._curvedFill || '#000000',
      stroke: src._curvedStroke || undefined,
      strokeWidth: src._curvedStrokeWidth || 0,
      charSpacing: src._curvedCharSpacing || 0,
      lineHeight: src._curvedLineHeight || 1.16,
      left: (src.left || 0) + 20,
      top: (src.top || 0) + 20,
      originX: 'center',
      originY: 'center',
    });
    this.canvas.add(tempText);
    this._createCurvedText(tempText, src._curvedType, {
      radiusFactor: src._curvedRadiusFactor || 1.0,
      spacing: src._curvedSpacing || 1.0,
    });
    // _createCurvedText übernimmt setActiveObject + Events selbst
  }

  _copyCurvedProps(src, dst) {
    if (!src || !dst) return;
    const props = ['_curvedText', '_curvedType', '_curvedFontSize', '_curvedFontFamily', '_curvedFontWeight', '_curvedFill', '_curvedStroke', '_curvedStrokeWidth', '_curvedRadiusFactor', '_curvedSpacing', '_curvedCharSpacing', '_curvedLineHeight', 'customName'];
    for (const p of props) {
      if (src[p] !== undefined) dst[p] = src[p];
    }
    // Recursively in case of activeSelection
    if (src._objects && dst._objects && Array.isArray(src._objects) && src._objects.length === dst._objects.length) {
      for (let i = 0; i < src._objects.length; i++) {
        this._copyCurvedProps(src._objects[i], dst._objects[i]);
      }
    }
  }

  groupSelected() {
    const active = this.canvas.getActiveObject();
    if (!active || active.type !== 'activeSelection') return;
    active.toGroup();
    active.customName = 'Gruppe';
    this.canvas.renderAll();
  }

  ungroupSelected() {
    const active = this.canvas.getActiveObject();
    if (!active || active.type !== 'group') return;
    active.toActiveSelection();
    this.canvas.renderAll();
  }

  lockSelected() {
    const active = this.canvas.getActiveObjects();
    active.forEach((obj) => {
      obj.set({
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        hasControls: false,
        selectable: true,
        isLocked: true,
      });
    });
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  unlockSelected() {
    const active = this.canvas.getActiveObjects();
    active.forEach((obj) => {
      obj.set({
        lockMovementX: false,
        lockMovementY: false,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false,
        hasControls: true,
        isLocked: false,
      });
    });
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  toggleLock() {
    const active = this.canvas.getActiveObjects();
    if (active.length === 0) return;
    const anyLocked = active.some((obj) => obj.isLocked);
    if (anyLocked) {
      this.unlockSelected();
    } else {
      this.lockSelected();
    }
  }

  // --- Alignment ---

  alignSelected(direction) {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    const canvasW = this.vWidth;
    const canvasH = this.vHeight;
    // Use absolute coords (skip viewport zoom) to match vWidth/vHeight
    const bound = active.getBoundingRect(true, true);

    switch (direction) {
      case 'left':
        active.set('left', active.left - bound.left);
        break;
      case 'right':
        active.set('left', active.left + (canvasW - bound.left - bound.width));
        break;
      case 'top':
        active.set('top', active.top - bound.top);
        break;
      case 'bottom':
        active.set('top', active.top + (canvasH - bound.top - bound.height));
        break;
      case 'center-h':
        active.set('left', active.left + (canvasW / 2 - bound.left - bound.width / 2));
        break;
      case 'center-v':
        active.set('top', active.top + (canvasH / 2 - bound.top - bound.height / 2));
        break;
      default:
        break;
    }
    active.setCoords();
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  // --- Transformations ---

  flipHorizontal() {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    active.set('flipX', !active.flipX);
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  flipVertical() {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    active.set('flipY', !active.flipY);
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  rotate(degrees = 90) {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    active.rotate((active.angle || 0) + degrees);
    active.setCoords();
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  // --- Layers ---

  bringForward() {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    this.canvas.bringForward(active);
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  sendBackward() {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    this.canvas.sendBackwards(active);
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  bringToFront() {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    this.canvas.bringToFront(active);
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  sendToBack() {
    const active = this.canvas.getActiveObject();
    if (!active) return;
    this.canvas.sendToBack(active);
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  // --- Canvas Settings ---

  setBackgroundColor(color) {
    this.canvas.backgroundColor = color;
    // Redraw overlays with contrast-aware colors
    this.drawBleedLines(this.bleedCm);
    this.drawHemLines(this.hemPerSide || this.hemCm);
    this.drawHohlsaum(this.hohlsaumConfig);
    this.drawGrommets(this.grommetsConfig, this.hemPerSide || this.hemCm);
    this.canvas.renderAll();
    const { bleedColor, hemColor } = this._getOverlayColors();
    eventBus.emit('overlay:colors', { bleedColor, hemColor });
    eventBus.emit('canvas:changed');
  }

  setBackgroundImage(url) {
    fabric.Image.fromURL(url, (img) => {
      if (!img) return;
      // vWidth/vHeight = virtuelle Groesse (volle Aufloesung, nicht skaliert)
      const cw = this.vWidth;
      const ch = this.vHeight;

      // Sofort als Data-URL speichern fuer SVG-Export (CORS-sicher)
      try {
        const el = img.getElement();
        const tmpC = document.createElement('canvas');
        tmpC.width = el.naturalWidth || el.width;
        tmpC.height = el.naturalHeight || el.height;
        const tmpCtx = tmpC.getContext('2d');
        tmpCtx.drawImage(el, 0, 0);
        this._bgImageDataUrl = tmpC.toDataURL('image/jpeg', 0.92);
      } catch (e) {
        console.warn('[setBackgroundImage] Could not cache data URL:', e);
        this._bgImageDataUrl = null;
      }

      // Bild exakt auf die gesamte Buehne strecken
      this.canvas.setBackgroundImage(img, () => {
        this.canvas.renderAll();
        eventBus.emit('canvas:changed');
        eventBus.emit('bgimage:changed', true);
      }, {
        scaleX: cw / img.width,
        scaleY: ch / img.height,
        left: 0,
        top: 0,
        originX: 'left',
        originY: 'top',
      });
    }, { crossOrigin: 'anonymous' });
  }

  removeBackgroundImage() {
    this.canvas.backgroundImage = null;
    this.canvas.backgroundColor = '#ffffff';
    this._bgImageDataUrl = null;
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
    eventBus.emit('bgimage:changed', false);
    eventBus.emit('bgcolor:changed', '#ffffff');
  }

  hasBackgroundImage() {
    return !!this.canvas.backgroundImage;
  }

  // Parse a CSS color to {r,g,b}
  _parseColor(color) {
    if (!color || color === 'transparent') return { r: 255, g: 255, b: 255 };
    // hex
    let m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
    if (m) return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
    m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(color);
    if (m) return { r: parseInt(m[1]+m[1], 16), g: parseInt(m[2]+m[2], 16), b: parseInt(m[3]+m[3], 16) };
    // rgb/rgba
    m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(color);
    if (m) return { r: +m[1], g: +m[2], b: +m[3] };
    return { r: 255, g: 255, b: 255 };
  }

  // Relative luminance (0=black, 1=white)
  _luminance(r, g, b) {
    const [rs, gs, bs] = [r, g, b].map((c) => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  }

  // Get overlay colors that contrast with the current background
  _getOverlayColors() {
    const bg = this._parseColor(this.canvas.backgroundColor);
    const lum = this._luminance(bg.r, bg.g, bg.b);

    // Check if specific color channels dominate
    const isReddish = bg.r > 150 && bg.r > bg.g * 1.3 && bg.r > bg.b * 1.3;
    const isBluish = bg.b > 150 && bg.b > bg.r * 1.3 && bg.b > bg.g * 1.3;

    let bleedColor, hemColor, bleedFill, hemFill;

    if (lum < 0.15) {
      // Very dark background → bright colors
      bleedColor = '#ff6b6b';
      hemColor = '#69b3ff';
      bleedFill = 'rgba(255, 107, 107, 0.08)';
      hemFill = 'rgba(105, 179, 255, 0.08)';
    } else if (isReddish) {
      // Red-ish background → use cyan/green for bleed
      bleedColor = '#00e5ff';
      hemColor = '#ffeb3b';
      bleedFill = 'rgba(0, 229, 255, 0.08)';
      hemFill = 'rgba(255, 235, 59, 0.08)';
    } else if (isBluish) {
      // Blue-ish background → use orange/yellow
      bleedColor = '#ff6d00';
      hemColor = '#ffea00';
      bleedFill = 'rgba(255, 109, 0, 0.08)';
      hemFill = 'rgba(255, 234, 0, 0.08)';
    } else if (lum > 0.7) {
      // Light background → standard dark-ish colors
      bleedColor = '#ef4444';
      hemColor = '#3b82f6';
      bleedFill = 'rgba(239, 68, 68, 0.06)';
      hemFill = 'rgba(59, 130, 246, 0.06)';
    } else {
      // Medium backgrounds → bright/white-ish overlay
      bleedColor = '#ff4757';
      hemColor = '#ffa502';
      bleedFill = 'rgba(255, 71, 87, 0.08)';
      hemFill = 'rgba(255, 165, 2, 0.08)';
    }

    return { bleedColor, hemColor, bleedFill, hemFill };
  }

  // Re-stack all overlay objects on top after any canvas change
  _bringOverlaysToFront() {
    const bringAll = (arr) => arr.forEach((o) => { if (o.canvas) this.canvas.bringToFront(o); });
    // Bring number outline texts to front (above collage placeholders/images)
    this.canvas.getObjects().filter((o) => o.isNumberOutline).forEach((o) => this.canvas.bringToFront(o));
    bringAll(this.bleedObjects);
    bringAll(this.hemObjects);
    bringAll(this.hohlsaumObjects);
    bringAll(this.grommetsObjects);
  }

  // --- Text Editing (external, avoids CSS zoom conflicts) ---

  updateText(textObj, newText, options = {}) {
    if (!textObj) return;
    textObj.set('text', newText);
    if (options.fontSize) textObj.set('fontSize', options.fontSize);
    if (options.fontFamily) textObj.set('fontFamily', options.fontFamily);
    if (options.fontWeight) textObj.set('fontWeight', options.fontWeight);
    if (options.fill) textObj.set('fill', options.fill);
    if (options.fontStyle) textObj.set('fontStyle', options.fontStyle);
    if (options.textAlign) textObj.set('textAlign', options.textAlign);
    // Stroke / Outline support — paint stroke behind fill for clean text outlines
    if ('stroke' in options) textObj.set('stroke', options.stroke || '');
    if ('strokeWidth' in options) textObj.set('strokeWidth', options.strokeWidth || 0);
    if (textObj.stroke && textObj.strokeWidth > 0) {
      textObj.set('paintFirst', 'stroke');
      textObj.set('strokeLineJoin', 'round');
      textObj.set('strokeLineCap', 'round');
    }
    // Shadow support
    if ('shadow' in options) {
      if (options.shadow) {
        textObj.set('shadow', new fabric.Shadow(options.shadow));
      } else {
        textObj.set('shadow', null);
      }
    }
    // Letter spacing (charSpacing) support
    if ('charSpacing' in options) textObj.set('charSpacing', options.charSpacing);
    // Line height support
    if ('lineHeight' in options) textObj.set('lineHeight', options.lineHeight);
    // Force recalculation — clear internal caches so charSpacing/lineHeight take effect
    if (textObj._clearCache) textObj._clearCache();
    textObj.dirty = true;
    // Bounding Box an neuen Text anpassen
    if (textObj.initDimensions) textObj.initDimensions();
    textObj.setCoords();
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  // --- Text Effects ---

  applyTextEffect(effect) {
    const obj = this.canvas.getActiveObject();
    if (!obj) return;
    const textTypes = ['i-text', 'text', 'textbox', 'extrusion3DIText', 'outlineStackIText', 'neonIText', 'retroIText'];
    const isText = textTypes.includes(obj.type);

    // Gebogenen/Wellen-Text zurück zu normalem Text konvertieren
    if (!isText && obj._curvedText) {
      if (effect === 'none') {
        const newText = new fabric.IText(obj._curvedText, {
          fontSize: obj._curvedFontSize || 40,
          fontFamily: obj._curvedFontFamily || 'Arial',
          fontWeight: obj._curvedFontWeight || 'normal',
          fill: obj._curvedFill || '#000000',
          stroke: obj._curvedStroke || '',
          strokeWidth: obj._curvedStrokeWidth || 0,
          charSpacing: obj._curvedCharSpacing || 0,
          lineHeight: obj._curvedLineHeight || 1.16,
          left: obj.left,
          top: obj.top,
          originX: 'center',
          originY: 'center',
          skewX: 0,
          skewY: 0,
        });
        if (obj.customName) newText.customName = obj.customName;
        this.canvas.remove(obj);
        this.canvas.add(newText);
        this.canvas.setActiveObject(newText);
        this.canvas.renderAll();
        eventBus.emit('object:selected', { target: [newText] });
        eventBus.emit('layers:changed');
        eventBus.emit('canvas:changed');
        return;
      }
      // Anderen Effekt auf gebogenen Text anwenden: erst zurückkonvertieren, dann Effekt
      const tempText = new fabric.IText(obj._curvedText, {
        fontSize: obj._curvedFontSize || 40,
        fontFamily: obj._curvedFontFamily || 'Arial',
        fontWeight: obj._curvedFontWeight || 'normal',
        fill: obj._curvedFill || '#000000',
        stroke: obj._curvedStroke || '',
        strokeWidth: obj._curvedStrokeWidth || 0,
        charSpacing: obj._curvedCharSpacing || 0,
        lineHeight: obj._curvedLineHeight || 1.16,
        left: obj.left,
        top: obj.top,
        originX: 'center',
        originY: 'center',
      });
      if (obj.customName) tempText.customName = obj.customName;
      this.canvas.remove(obj);
      this.canvas.add(tempText);
      this.canvas.setActiveObject(tempText);
      // Weiter mit dem neuen Textobjekt
      return this.applyTextEffect(effect);
    }

    if (!isText) return;

    // Reset all effects first
    obj.set({ skewX: 0, skewY: 0, scaleX: 1, scaleY: 1 });

    switch (effect) {
      case 'none':
        // Already reset above
        break;
      case 'skew-right':
        obj.set({ skewX: 20 });
        break;
      case 'skew-left':
        obj.set({ skewX: -20 });
        break;
      case 'wide':
        obj.set({ scaleX: (obj.scaleX || 1) * 1.5 });
        break;
      case 'tall':
        obj.set({ scaleY: (obj.scaleY || 1) * 1.5 });
        break;
      case 'squeeze':
        obj.set({ scaleX: (obj.scaleX || 1) * 0.6 });
        break;
      case 'italic-skew':
        obj.set({ skewX: 12 });
        break;
      case 'perspective-left':
        obj.set({ skewY: 10 });
        break;
      case 'perspective-right':
        obj.set({ skewY: -10 });
        break;
      case 'arc-top':
      case 'arc-bottom':
      case 'circle':
      case 'wave':
        this._createCurvedText(obj, effect);
        return; // Don't renderAll twice
      default:
        break;
    }

    obj.setCoords();
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  /**
   * Gebogenen Text erstellen.
   * @param {fabric.Text} textObj — Quell-Textobjekt (wird entfernt)
   * @param {string} curveType — 'arc-top', 'arc-bottom', 'circle', 'wave'
   * @param {object} curveOpts — { radiusFactor, spacing }
   */
  _createCurvedText(textObj, curveType, curveOpts = {}) {
    const text = textObj.text || 'Text';
    const fontSize = textObj.fontSize || 40;
    const fill = textObj.fill || '#000000';
    const fontFamily = textObj.fontFamily || 'Arial';
    const fontWeight = textObj.fontWeight || 'normal';
    const left = textObj.left;
    const top = textObj.top;
    const stroke = textObj.stroke || '';
    const strokeWidth = textObj.strokeWidth || 0;

    // Einstellbare Parameter mit Defaults
    const radiusFactor = curveOpts.radiusFactor ?? 1.0; // 0.3 = eng, 1.0 = normal, 3.0 = flach
    const spacing = curveOpts.spacing ?? 1.0; // Zeichenabstand-Faktor

    // Actual character width measurement instead of fixed estimate
    const measureCtx = document.createElement('canvas').getContext('2d');
    measureCtx.font = `${fontWeight || 'normal'} ${fontSize}px ${fontFamily}`;
    let totalWidth = 0;
    const charWidths = [];
    for (let i = 0; i < text.length; i++) {
      const w = measureCtx.measureText(text[i]).width * spacing;
      charWidths.push(w);
      totalWidth += w;
    }

    let baseRadius, startAngle, angleSpan;
    switch (curveType) {
      case 'arc-top':
        baseRadius = Math.max(totalWidth * 0.8, fontSize * 5);
        baseRadius *= radiusFactor;
        angleSpan = totalWidth / baseRadius;
        startAngle = -Math.PI / 2 - angleSpan / 2;
        break;
      case 'arc-bottom':
        baseRadius = Math.max(totalWidth * 0.8, fontSize * 5);
        baseRadius *= radiusFactor;
        angleSpan = totalWidth / baseRadius;
        // Lese-Richtung: links→rechts. Buchstaben sind UNTER dem Mittelpunkt,
        // also Sweep im Uhrzeigersinn (negative Richtung).
        startAngle = Math.PI / 2 + angleSpan / 2;
        angleSpan = -angleSpan;
        break;
      case 'circle':
        baseRadius = Math.max(totalWidth / (Math.PI * 1.8), fontSize * 3);
        baseRadius *= radiusFactor;
        angleSpan = Math.PI * 2;
        startAngle = -Math.PI / 2;
        break;
      case 'wave': {
        // Wave uses a sinusoidal path — NOT a circular arc.
        // Characters are placed left-to-right with gentle vertical oscillation.
        const waveAmplitude = fontSize * 0.5 * radiusFactor; // subtle wave height
        const waveCycles = 1.0; // one full sine wave across the text
        const waveGroup = [];

        for (let i = 0; i < text.length; i++) {
          let precedingWidth = 0;
          for (let j = 0; j < i; j++) precedingWidth += charWidths[j];
          const charCenter = precedingWidth + charWidths[i] / 2;
          // Normalise position along text (0..1)
          const t = charCenter / totalWidth;
          // X: linear left-to-right, centered around 0
          const wx = charCenter - totalWidth / 2;
          // Y: sinusoidal oscillation
          const wy = Math.sin(t * Math.PI * 2 * waveCycles) * waveAmplitude;
          // Rotation: tangent angle of sine curve for natural tilt
          const dydx = (Math.PI * 2 * waveCycles / totalWidth) * waveAmplitude * Math.cos(t * Math.PI * 2 * waveCycles);
          const rotDeg = Math.atan(dydx) * (180 / Math.PI);

          const charText = new fabric.Text(text[i], {
            fontSize,
            fontFamily,
            fontWeight,
            fill,
            stroke: stroke || undefined,
            strokeWidth: strokeWidth || 0,
            left: wx,
            top: wy,
            angle: rotDeg,
            originX: 'center',
            originY: 'center',
          });
          waveGroup.push(charText);
        }

        if (waveGroup.length === 0) return;

        const waveGroupObj = new fabric.Group(waveGroup, {
          left,
          top,
          originX: 'center',
          originY: 'center',
        });

        waveGroupObj.customName = text;
        waveGroupObj._curvedText = text;
        waveGroupObj._curvedType = curveType;
        waveGroupObj._curvedFontSize = fontSize;
        waveGroupObj._curvedFontFamily = fontFamily;
        waveGroupObj._curvedFontWeight = fontWeight;
        waveGroupObj._curvedFill = fill;
        waveGroupObj._curvedStroke = stroke;
        waveGroupObj._curvedStrokeWidth = strokeWidth;
        waveGroupObj._curvedRadiusFactor = radiusFactor;
        waveGroupObj._curvedSpacing = spacing;
        waveGroupObj._curvedCharSpacing = textObj.charSpacing || 0;
        waveGroupObj._curvedLineHeight = textObj.lineHeight || 1.16;

        this.canvas.remove(textObj);
        this.canvas.add(waveGroupObj);
        this.canvas.setActiveObject(waveGroupObj);
        this.canvas.renderAll();
        eventBus.emit('object:selected', { target: [waveGroupObj] });
        eventBus.emit('layers:changed');
        eventBus.emit('canvas:changed');
        return; // wave is handled completely here
      }
      default:
        return;
    }

    const group = [];

    for (let i = 0; i < text.length; i++) {
      // Calculate angle based on measured character widths
      let precedingWidth = 0;
      for (let j = 0; j < i; j++) precedingWidth += charWidths[j];
      const charCenter = precedingWidth + charWidths[i] / 2;
      const angle = startAngle + (charCenter / totalWidth) * angleSpan;
      const x = baseRadius * Math.cos(angle);
      const y = baseRadius * Math.sin(angle);
      // arc-bottom: Buchstaben unter dem Kreismittelpunkt — radiale Achse zeigt nach UNTEN.
      // Damit der Buchstabe aufrecht steht, muss die Tangente gespiegelt werden (− statt +).
      const rotation = (curveType === 'arc-bottom') ? (angle - Math.PI / 2) : (angle + Math.PI / 2);

      const charText = new fabric.Text(text[i], {
        fontSize,
        fontFamily,
        fontWeight,
        fill,
        stroke: stroke || undefined,
        strokeWidth: strokeWidth || 0,
        left: x,
        top: y,
        angle: (rotation * 180) / Math.PI,
        originX: 'center',
        originY: 'center',
      });
      group.push(charText);
    }

    if (group.length === 0) return;

    const curvedGroup = new fabric.Group(group, {
      left,
      top,
      originX: 'center',
      originY: 'center',
    });

    // Alle Parameter speichern für spätere Bearbeitung
    curvedGroup.customName = text;
    curvedGroup._curvedText = text;
    curvedGroup._curvedType = curveType;
    curvedGroup._curvedFontSize = fontSize;
    curvedGroup._curvedFontFamily = fontFamily;
    curvedGroup._curvedFontWeight = fontWeight;
    curvedGroup._curvedFill = fill;
    curvedGroup._curvedStroke = stroke;
    curvedGroup._curvedStrokeWidth = strokeWidth;
    curvedGroup._curvedRadiusFactor = radiusFactor;
    curvedGroup._curvedSpacing = spacing;
    curvedGroup._curvedCharSpacing = textObj.charSpacing || 0;
    curvedGroup._curvedLineHeight = textObj.lineHeight || 1.16;

    this.canvas.remove(textObj);
    this.canvas.add(curvedGroup);
    this.canvas.setActiveObject(curvedGroup);
    this.canvas.renderAll();
    eventBus.emit('object:selected', { target: [curvedGroup] });
    eventBus.emit('layers:changed');
    eventBus.emit('canvas:changed');
  }

  updateCurvedText(groupObj, newText, options = {}) {
    if (!groupObj) return;
    const curveType = options.curveType || groupObj._curvedType || 'arc-top';
    const left = groupObj.left;
    const top = groupObj.top;
    const text = (typeof newText === 'string' && newText.length > 0) ? newText : (groupObj._curvedText || 'Text');

    const tempText = new fabric.Text(text, {
      fontSize: options.fontSize || groupObj._curvedFontSize || 40,
      fontFamily: options.fontFamily || groupObj._curvedFontFamily || 'Arial',
      fontWeight: options.fontWeight || groupObj._curvedFontWeight || 'normal',
      fill: options.fill || groupObj._curvedFill || '#000000',
      stroke: options.stroke ?? groupObj._curvedStroke ?? '',
      strokeWidth: options.strokeWidth ?? groupObj._curvedStrokeWidth ?? 0,
      charSpacing: options.charSpacing ?? groupObj._curvedCharSpacing ?? 0,
      lineHeight: options.lineHeight ?? groupObj._curvedLineHeight ?? 1.16,
      left,
      top,
      originX: 'center',
      originY: 'center',
    });

    // Defensive: Alle Objekte, die das gleiche curvedText am gleichen Punkt sind, entfernen
    const stale = this.canvas.getObjects().filter(o =>
      o === groupObj ||
      (o._curvedText && o._curvedText === groupObj._curvedText && Math.abs((o.left||0) - left) < 1 && Math.abs((o.top||0) - top) < 1)
    );
    stale.forEach(o => this.canvas.remove(o));

    this.canvas.add(tempText);
    this.canvas.setActiveObject(tempText);
    this._createCurvedText(tempText, curveType, {
      radiusFactor: options.radiusFactor ?? groupObj._curvedRadiusFactor ?? 1.0,
      spacing: options.spacing ?? groupObj._curvedSpacing ?? 1.0,
    });
  }

  // --- Grid ---

  toggleGrid() {
    this.gridVisible = !this.gridVisible;
    // Grid rendering is now handled by CanvasArea CSS overlay
    eventBus.emit('grid:changed', { visible: this.gridVisible, snapEnabled: false });
    return this.gridVisible;
  }

  // Grid rendering is now handled by CanvasArea CSS overlay (GridOverlay component)
  // _drawGrid and _removeGrid are no longer needed

  // --- Bleed Lines ---

  drawBleedLines(bleedCm = 1) {
    this._removeBleedLines();
    this.bleedCm = bleedCm;
    if (bleedCm <= 0) return;
    const bleedPx = bleedCm * PX_PER_CM;
    const width = this.vWidth;
    const height = this.vHeight;
    const { bleedColor } = this._getOverlayColors();
    const shortest = Math.min(width, height);
    const sw = Math.max(1.5, shortest / 800);
    const dash = [sw * 4, sw * 3];

    const lineProps = {
      stroke: bleedColor,
      strokeWidth: sw,
      strokeDashArray: dash,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      isBleed: true,
    };

    const lines = [
      new fabric.Line([bleedPx, 0, bleedPx, height], { ...lineProps }),
      new fabric.Line([width - bleedPx, 0, width - bleedPx, height], { ...lineProps }),
      new fabric.Line([0, bleedPx, width, bleedPx], { ...lineProps }),
      new fabric.Line([0, height - bleedPx, width, height - bleedPx], { ...lineProps }),
    ];

    lines.forEach((line) => {
      this.bleedObjects.push(line);
      this.canvas.add(line);
      this.canvas.bringToFront(line);
    });

  }

  _removeBleedLines() {
    this.bleedObjects.forEach((obj) => this.canvas.remove(obj));
    this.bleedObjects = [];
  }

  // --- Hem (Saum) Lines ---

  drawHemLines(hemCm = 0) {
    this._removeHemLines();
    // Accept per-side object { top, bottom, left, right } or single number
    const perSide = typeof hemCm === 'object'
      ? hemCm
      : { top: hemCm, bottom: hemCm, left: hemCm, right: hemCm };
    // Store max for backwards compat
    this.hemCm = Math.max(perSide.top || 0, perSide.bottom || 0, perSide.left || 0, perSide.right || 0);
    this.hemPerSide = perSide;

    const width = this.vWidth;
    const height = this.vHeight;
    const { hemColor } = this._getOverlayColors();
    const shortest = Math.min(width, height);
    const sw = Math.max(1.5, shortest / 800);
    const dash = [sw * 4, sw * 3];

    const lineProps = {
      stroke: hemColor,
      strokeWidth: sw,
      strokeDashArray: dash,
      selectable: false,
      evented: false,
      excludeFromExport: true,
      isHem: true,
    };

    const addLine = (line) => {
      this.hemObjects.push(line);
      this.canvas.add(line);
      this.canvas.bringToFront(line);
    };

    if (perSide.left > 0) {
      const offset = (this.bleedCm + perSide.left) * PX_PER_CM;
      addLine(new fabric.Line([offset, 0, offset, height], { ...lineProps }));
    }
    if (perSide.right > 0) {
      const offset = (this.bleedCm + perSide.right) * PX_PER_CM;
      addLine(new fabric.Line([width - offset, 0, width - offset, height], { ...lineProps }));
    }
    if (perSide.top > 0) {
      const offset = (this.bleedCm + perSide.top) * PX_PER_CM;
      addLine(new fabric.Line([0, offset, width, offset], { ...lineProps }));
    }
    if (perSide.bottom > 0) {
      const offset = (this.bleedCm + perSide.bottom) * PX_PER_CM;
      addLine(new fabric.Line([0, height - offset, width, height - offset], { ...lineProps }));
    }
  }

  _removeHemLines() {
    this.hemObjects.forEach((obj) => this.canvas.remove(obj));
    this.hemObjects = [];
  }

  // --- Hohlsaum (Tunnel) ---

  drawHohlsaum(config = { top: 0, bottom: 0, left: 0, right: 0 }) {
    this._removeHohlsaum();
    this.hohlsaumConfig = config;

    const width = this.vWidth;
    const height = this.vHeight;
    const bleedPx = this.bleedCm * PX_PER_CM;

    const bg = this._parseColor(this.canvas.backgroundColor);
    const lum = this._luminance(bg.r, bg.g, bg.b);
    const tunnelColor = lum < 0.3 ? '#a78bfa' : '#7c3aed'; // purple, contrasts with red/blue

    const shortest = Math.min(width, height);
    const sw = Math.max(1.5, shortest / 600);
    const lineProps = {
      stroke: tunnelColor,
      strokeWidth: sw,
      strokeDashArray: [sw * 4, sw * 2],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      isHohlsaum: true,
    };

    const sides = [
      { key: 'top', val: config.top },
      { key: 'bottom', val: config.bottom },
      { key: 'left', val: config.left },
      { key: 'right', val: config.right },
    ];

    sides.forEach(({ key, val }) => {
      if (!val || val <= 0) return;
      const tunnelPx = val * PX_PER_CM;
      const offset = bleedPx + tunnelPx;

      // Two parallel lines to indicate the tunnel
      let line1, line2;
      if (key === 'top') {
        line1 = new fabric.Line([0, bleedPx, width, bleedPx], { ...lineProps });
        line2 = new fabric.Line([0, offset, width, offset], { ...lineProps });
      } else if (key === 'bottom') {
        line1 = new fabric.Line([0, height - bleedPx, width, height - bleedPx], { ...lineProps });
        line2 = new fabric.Line([0, height - offset, width, height - offset], { ...lineProps });
      } else if (key === 'left') {
        line1 = new fabric.Line([bleedPx, 0, bleedPx, height], { ...lineProps });
        line2 = new fabric.Line([offset, 0, offset, height], { ...lineProps });
      } else if (key === 'right') {
        line1 = new fabric.Line([width - bleedPx, 0, width - bleedPx, height], { ...lineProps });
        line2 = new fabric.Line([width - offset, 0, width - offset, height], { ...lineProps });
      }

      [line1, line2].forEach((l) => {
        if (!l) return;
        this.hohlsaumObjects.push(l);
        this.canvas.add(l);
        this.canvas.bringToFront(l);
      });
    });
  }

  _removeHohlsaum() {
    this.hohlsaumObjects.forEach((obj) => this.canvas.remove(obj));
    this.hohlsaumObjects = [];
  }

  // --- Grommets (Oesen) ---

  // config.sides = { top: bool, bottom: bool, left: bool, right: bool } — which sides have grommets
  // config.hemPerSide = { top: cm, bottom: cm, left: cm, right: cm } — hem per side for inset calc
  drawGrommets(config, hemCm = 0) {
    this._removeGrommets();
    this.grommetsConfig = config;
    if (!config || !config.enabled) return;

    const width = this.vWidth;
    const height = this.vHeight;

    // Per-side hem values
    const hemPS = config.hemPerSide || (typeof hemCm === 'object' ? hemCm : { top: hemCm, bottom: hemCm, left: hemCm, right: hemCm });

    // Per-side inset: with hem → bleed + hem/2, without hem → bleed + 1.5cm
    const insetCalc = (sideHem) => sideHem > 0
      ? (this.bleedCm + sideHem / 2) * PX_PER_CM
      : (this.bleedCm + 1.5) * PX_PER_CM;
    const insetTop = insetCalc(hemPS.top || 0);
    const insetBottom = insetCalc(hemPS.bottom || 0);
    const insetLeft = insetCalc(hemPS.left || 0);
    const insetRight = insetCalc(hemPS.right || 0);

    // Which sides have grommets (default: all)
    const sides = config.sides || { top: true, bottom: true, left: true, right: true };

    const innerDiameterMm = config.radiusMm || 11;
    const outerDiameterMm = innerDiameterMm * (22.5 / 11.6);
    const innerRadiusPx = (innerDiameterMm / 2 / 10) * PX_PER_CM;
    const outerRadiusPx = (outerDiameterMm / 2 / 10) * PX_PER_CM;

    const positions = this._calcGrommetPositions(config, width, height, { insetTop, insetBottom, insetLeft, insetRight }, sides);

    const bgLum = this._luminance(...Object.values(this._parseColor(this.canvas.backgroundColor)));
    const grommetStroke = bgLum < 0.3 ? '#cccccc' : '#666666';
    const grommetFill = bgLum < 0.3 ? 'rgba(220, 220, 220, 0.35)' : 'rgba(160, 160, 160, 0.3)';
    const holeFill = bgLum < 0.3 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.85)';
    const holeStroke = bgLum < 0.3 ? '#aaaaaa' : '#888888';

    positions.forEach(({ x, y }) => {
      const ring = new fabric.Circle({
        radius: outerRadiusPx,
        fill: grommetFill,
        stroke: grommetStroke,
        strokeWidth: Math.max(1, outerRadiusPx * 0.1),
        left: x - outerRadiusPx,
        top: y - outerRadiusPx,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        isGrommet: true,
      });
      const hole = new fabric.Circle({
        radius: innerRadiusPx,
        fill: holeFill,
        stroke: holeStroke,
        strokeWidth: Math.max(0.5, innerRadiusPx * 0.06),
        left: x - innerRadiusPx,
        top: y - innerRadiusPx,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        isGrommet: true,
      });
      this.grommetsObjects.push(ring, hole);
      this.canvas.add(ring);
      this.canvas.add(hole);
      this.canvas.bringToFront(ring);
      this.canvas.bringToFront(hole);
    });
  }

  _removeGrommets() {
    this.grommetsObjects.forEach((obj) => this.canvas.remove(obj));
    this.grommetsObjects = [];
  }

  _calcGrommetPositions(config, width, height, insets, sides) {
    const { insetTop, insetBottom, insetLeft, insetRight } = insets;
    const positions = [];
    const seen = new Set();

    const addPos = (x, y) => {
      const key = `${Math.round(x)},${Math.round(y)}`;
      if (!seen.has(key)) {
        seen.add(key);
        positions.push({ x, y });
      }
    };

    // Corners — only add if at least one adjacent side has grommets
    if (sides.top || sides.left) addPos(insetLeft, insetTop);
    if (sides.top || sides.right) addPos(width - insetRight, insetTop);
    if (sides.bottom || sides.left) addPos(insetLeft, height - insetBottom);
    if (sides.bottom || sides.right) addPos(width - insetRight, height - insetBottom);

    if (config.mode === 'corners') return positions;

    const spacingCm = config.customSpacingCm || 50;
    const spacingPx = spacingCm * PX_PER_CM;

    // Top edge
    if (sides.top) {
      const topLen = (width - insetLeft - insetRight);
      if (topLen > spacingPx) {
        const count = Math.round(topLen / spacingPx);
        const step = topLen / count;
        for (let i = 1; i < count; i++) addPos(insetLeft + i * step, insetTop);
      }
    }

    // Bottom edge
    if (sides.bottom) {
      const botLen = (width - insetLeft - insetRight);
      if (botLen > spacingPx) {
        const count = Math.round(botLen / spacingPx);
        const step = botLen / count;
        for (let i = 1; i < count; i++) addPos(insetLeft + i * step, height - insetBottom);
      }
    }

    // Left edge
    if (sides.left) {
      const leftLen = (height - insetTop - insetBottom);
      if (leftLen > spacingPx) {
        const count = Math.round(leftLen / spacingPx);
        const step = leftLen / count;
        for (let i = 1; i < count; i++) addPos(insetLeft, insetTop + i * step);
      }
    }

    // Right edge
    if (sides.right) {
      const rightLen = (height - insetTop - insetBottom);
      if (rightLen > spacingPx) {
        const count = Math.round(rightLen / spacingPx);
        const step = rightLen / count;
        for (let i = 1; i < count; i++) addPos(width - insetRight, insetTop + i * step);
      }
    }

    return positions;
  }

  // --- Serialization ---

  getJSON() {
    const json = this.canvas.toJSON(['customName', 'isLocked', 'isGrid', 'isBleed', 'isHem', 'isHohlsaum', 'isGrommet', 'isGuide', 'guideOrientation', 'guidePositionPx', 'excludeFromExport', '_curvedText', '_curvedType', '_curvedFontSize', '_curvedFontFamily', '_curvedFontWeight', '_curvedFill', '_curvedStroke', '_curvedStrokeWidth', '_curvedRadiusFactor', '_curvedSpacing']);
    // Convert external image URLs to data URLs so designs survive URL expiry
    if (json.objects) {
      json.objects.forEach((obj) => {
        if (obj.type === 'image' && obj.src && !obj.src.startsWith('data:')) {
          // Find the corresponding fabric object and get its data URL
          const fabricObj = this.canvas.getObjects().find(
            (o) => o.type === 'image' && o._element && o.getSrc && o.getSrc() === obj.src
          );
          if (fabricObj && fabricObj._element) {
            try {
              const tmpCanvas = document.createElement('canvas');
              tmpCanvas.width = fabricObj._element.naturalWidth || fabricObj._element.width;
              tmpCanvas.height = fabricObj._element.naturalHeight || fabricObj._element.height;
              const ctx = tmpCanvas.getContext('2d');
              ctx.drawImage(fabricObj._element, 0, 0);
              obj.src = tmpCanvas.toDataURL('image/png');
            } catch (e) {
              console.warn('Could not convert image to data URL (CORS?):', e.message);
            }
          }
        }
      });
    }
    return json;
  }

  async loadJSON(json) {
    // Parse JSON string if needed
    if (typeof json === 'string') {
      try { json = JSON.parse(json); } catch (e) {
        console.error('loadJSON parse error:', e, '\nFirst 200 chars:', String(json).substring(0, 200));
        // Throw instead of silent return so caller can show error toast
        throw new Error('Design-Daten konnten nicht geparst werden: ' + e.message);
      }
    }
    if (!json || typeof json !== 'object') {
      console.warn('loadJSON: invalid JSON payload', json);
      throw new Error('Ungültige Design-Daten');
    }

    // Pre-load ALL fonts used in the design BEFORE loadFromJSON
    // This prevents Fabric.js from measuring text with fallback fonts,
    // which causes text to be cut off (wrong width measurement).
    if (json && json.objects) {
      const fontsToLoad = new Set();
      // Also scan groups/nested objects recursively
      const scanObjects = (objects) => {
        if (!objects) return;
        objects.forEach(obj => {
          if (obj.fontFamily) fontsToLoad.add(obj.fontFamily);
          // Check per-character styles (textbox style overrides)
          if (obj.styles) {
            try {
              Object.values(obj.styles).forEach(line => {
                if (line) Object.values(line).forEach(cs => {
                  if (cs && cs.fontFamily) fontsToLoad.add(cs.fontFamily);
                });
              });
            } catch {}
          }
          // Recurse into groups
          if (obj.objects) scanObjects(obj.objects);
        });
      };
      scanObjects(json.objects);

      // Remove system/generic fonts that don't need loading
      const SYSTEM_FONTS = new Set([
        'Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia',
        'Verdana', 'Trebuchet MS', 'Palatino', 'Comic Sans MS', 'Impact',
        'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy'
      ]);
      SYSTEM_FONTS.forEach(sf => fontsToLoad.delete(sf));

      if (fontsToLoad.size > 0) {
        console.log('[CanvasManager] Pre-loading fonts:', [...fontsToLoad].join(', '));

        // Step 1a: Google Fonts laden
        const googleFonts = [...fontsToLoad].filter(f => !f.includes('/') && !SYSTEM_FONTS.has(f));
        if (googleFonts.length > 0) {
          const googleUrl = 'https://fonts.googleapis.com/css2?' + googleFonts.map(f => 'family=' + encodeURIComponent(f) + ':wght@400;700').join('&') + '&display=swap';
          if (!document.querySelector(`link[href*="fonts.googleapis.com"][data-preload]`)) {
            const gLink = document.createElement('link');
            gLink.rel = 'stylesheet';
            gLink.href = googleUrl;
            gLink.dataset.preload = '1';
            document.head.appendChild(gLink);
            await Promise.race([
              new Promise(resolve => { gLink.onload = resolve; gLink.onerror = resolve; }),
              new Promise(resolve => setTimeout(resolve, 3000))
            ]);
            console.log('[CanvasManager] Google Fonts CSS loaded:', googleFonts.join(', '));
          }
        }

        // Step 1b: Custom font @font-face CSS laden (eigene Fonts via API)
        if (!document.getElementById('custom-fonts-css')) {
          try {
            const apiBase = process.env.REACT_APP_API_URL || (window.location.pathname.includes('designer')
              ? window.location.origin + '/designer/api'
              : window.location.origin + '/api');
            const link = document.createElement('link');
            link.id = 'custom-fonts-css';
            link.rel = 'stylesheet';
            link.href = apiBase + '/fonts/css';
            document.head.appendChild(link);
            await Promise.race([
              new Promise(resolve => { link.onload = resolve; link.onerror = resolve; }),
              new Promise(resolve => setTimeout(resolve, 2000))
            ]);
          } catch (e) {
            console.warn('[CanvasManager] Could not load custom font CSS:', e);
          }
        }

        // Step 2: Load each font via document.fonts.load() with a 3-second global timeout
        const FONT_TIMEOUT_MS = 3000;
        const fontLoadWork = async () => {
          const fontPromises = [];
          fontsToLoad.forEach(f => {
            // Load multiple weights/styles to cover all variants used in the design
            ['normal', 'bold', 'italic', '700', '400'].forEach(variant => {
              fontPromises.push(
                document.fonts.load(`${variant} 40px "${f}"`).catch(() => null)
              );
            });
          });
          await Promise.all(fontPromises);

          // Wait for document.fonts.ready (ensures browser has fully processed all @font-face)
          await document.fonts.ready;

          // Verify which fonts actually loaded
          fontsToLoad.forEach(f => {
            const loaded = document.fonts.check(`16px "${f}"`);
            if (!loaded) {
              console.warn(`[CanvasManager] Font "${f}" could not be loaded — will use fallback`);
            }
          });
        };

        // Race font loading against timeout so we never hang
        await Promise.race([
          fontLoadWork(),
          new Promise(resolve => setTimeout(() => {
            console.warn(`[CanvasManager] Font preload timed out after ${FONT_TIMEOUT_MS}ms`);
            resolve();
          }, FONT_TIMEOUT_MS))
        ]);

        // Echte Verifikation: Warten bis sich die gemessene Textbreite ändert
        // (beweist dass der Font tatsächlich geladen und anwendbar ist)
        const verifyFontLoaded = (fontName) => {
          return new Promise(resolve => {
            const testCanvas = document.createElement('canvas');
            const ctx = testCanvas.getContext('2d');
            ctx.font = `40px monospace`;
            const fallbackWidth = ctx.measureText('MMMMWWWW').width;
            ctx.font = `40px "${fontName}", monospace`;
            const customWidth = ctx.measureText('MMMMWWWW').width;
            if (Math.abs(customWidth - fallbackWidth) > 1) {
              resolve(true); // Font ist geladen
              return;
            }
            // Noch nicht geladen — pollen
            let attempts = 0;
            const check = () => {
              ctx.font = `40px "${fontName}", monospace`;
              const w = ctx.measureText('MMMMWWWW').width;
              if (Math.abs(w - fallbackWidth) > 1 || attempts >= 20) {
                resolve(attempts < 20);
                return;
              }
              attempts++;
              setTimeout(check, 150);
            };
            check();
          });
        };

        await Promise.all([...fontsToLoad].map(f => verifyFontLoaded(f)));
        console.log('[CanvasManager] Font preloading verified and complete');
      }
    }

    // Pre-validate external image URLs — replace broken ones with placeholder
    // so Fabric.js loadFromJSON doesn't hang on expired/broken URLs
    // WICHTIG: crossOrigin MUSS im JSON-Objekt stehen damit Fabric es beim Laden setzt!
    if (json && json.objects) {
      const PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQABNjN9GQAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAA0lEQVQI12P4z8BQDwAEgAF/QualzQAAAABJRU5ErkJggg==';
      const checks = json.objects.map((obj) => {
        if (obj.type === 'image' && obj.src && !obj.src.startsWith('data:')) {
          // crossOrigin im JSON setzen BEVOR loadFromJSON aufgerufen wird
          obj.crossOrigin = 'anonymous';
          return new Promise((res) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            const timer = setTimeout(() => { img.src = ''; res(false); }, 3000);
            img.onload = () => { clearTimeout(timer); res(true); };
            img.onerror = () => { clearTimeout(timer); res(false); };
            img.src = obj.src;
          }).then((ok) => {
            if (!ok) {
              console.warn('Image URL broken, using placeholder:', obj.src.substring(0, 80));
              obj._originalSrc = obj.src;
              obj.src = PLACEHOLDER;
              obj.customName = (obj.customName || 'Bild') + ' (nicht verfuegbar)';
            }
          });
        }
        return Promise.resolve();
      });
      await Promise.all(checks);
    }

    // Auch Hintergrundbild mit CORS markieren
    if (json && json.backgroundImage && json.backgroundImage.src && !json.backgroundImage.src.startsWith('data:')) {
      json.backgroundImage.crossOrigin = 'anonymous';
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('loadJSON timeout — some objects may not have loaded');
        this._afterLoadJSON();
        resolve();
      }, 10000);

      this.canvas.loadFromJSON(json, () => {
        clearTimeout(timeout);

        // Re-hydrate curved text groups + disable inline editing on all text
        this.canvas.getObjects().forEach(obj => {
          if (obj._curvedText && obj.type === 'group') {
            obj.set('hasControls', true);
            obj.set('lockUniScaling', false);
          }
          // Disable inline editing for ALL text objects (CSS zoom conflict)
          if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') {
            obj.set('editable', false);
          }
        });

        this._afterLoadJSON();
        resolve();
      });
    });
  }

  /**
   * Redraw overlay objects (bleed lines, hem, grommets, hohlsaum).
   * Used by HistoryManager after restoring a state that had overlays stripped out.
   */
  _redrawOverlays() {
    const toRemove = this.canvas.getObjects().filter((o) => o.isGrid || o.isBleed || o.isHem || o.isHohlsaum || o.isGrommet);
    toRemove.forEach((o) => this.canvas.remove(o));
    this.gridObjects = [];
    this.bleedObjects = [];
    this.hemObjects = [];
    this.hohlsaumObjects = [];
    this.grommetsObjects = [];
    this.drawBleedLines(this.bleedCm);
    this.drawHemLines(this.hemPerSide || this.hemCm);
    this.drawHohlsaum(this.hohlsaumConfig);
    this.drawGrommets(this.grommetsConfig, this.hemPerSide || this.hemCm);
  }

  _afterLoadJSON() {
    // Fix font rendering: Text-Objekte komplett neu erstellen damit Fabric.js
    // die Zeichenbreiten mit dem geladenen Font misst statt mit dem Fallback.
    const rebuildTextObjects = () => {
      const objects = this.canvas.getObjects();
      const textObjects = objects.filter(obj =>
        obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text'
      );

      textObjects.forEach(obj => {
        // Alle Properties sichern
        const props = obj.toObject();
        const index = objects.indexOf(obj);

        // Altes Objekt entfernen
        this.canvas.remove(obj);

        // Neues Objekt erstellen OHNE die alte width — Fabric.js berechnet sie neu
        const TextClass = obj.type === 'textbox' ? fabric.Textbox :
                         obj.type === 'i-text' ? fabric.IText : fabric.Text;

        const savedWidth = props.width;
        if (obj.type !== 'textbox') {
          delete props.width; // Width neu berechnen lassen
        }

        const newObj = new TextClass(props.text, {
          ...props,
          dirty: true,
        });

        // Bei i-text: Wenn die neu berechnete Width kleiner als die gespeicherte ist,
        // die gespeicherte verwenden (Font war beim Speichern korrekt geladen)
        if (obj.type !== 'textbox' && savedWidth && newObj.width < savedWidth * 0.9) {
          newObj.set('width', savedWidth);
        }

        // An der gleichen Position einfügen
        this.canvas.insertAt(newObj, index);
      });

      if (textObjects.length > 0) {
        this.canvas.requestRenderAll();
        console.log(`[_afterLoadJSON] ${textObjects.length} text objects rebuilt for correct font rendering`);
      }
    };

    // Warten bis Fonts wirklich bereit sind, dann Text-Objekte neu bauen
    const doRebuild = () => {
      rebuildTextObjects();
    };

    // Sofort ausführen
    doRebuild();

    // Nochmal nach fonts.ready als Sicherheitsnetz
    if (document.fonts) {
      document.fonts.ready.then(() => {
        setTimeout(() => {
          rebuildTextObjects();
        }, 200);
      });
    }

    // Fix CORS on background image loaded from JSON (prevents tainted canvas)
    const bgImg = this.canvas.backgroundImage;
    if (bgImg && bgImg.getElement) {
      const el = bgImg.getElement();
      const src = el ? el.src : null;
      if (src && !src.startsWith('data:')) {
        // Reload with CORS and stretch to fill entire canvas
        const cw = this.vWidth;
        const ch = this.vHeight;
        fabric.Image.fromURL(src, (newImg) => {
          if (newImg) {
            // Data-URL cachen fuer SVG-Export
            try {
              const nel = newImg.getElement();
              const tmpC = document.createElement('canvas');
              tmpC.width = nel.naturalWidth || nel.width;
              tmpC.height = nel.naturalHeight || nel.height;
              const tmpCtx = tmpC.getContext('2d');
              tmpCtx.drawImage(nel, 0, 0);
              this._bgImageDataUrl = tmpC.toDataURL('image/jpeg', 0.92);
            } catch (e) {
              console.warn('[_afterLoadJSON] Could not cache bg data URL:', e);
              this._bgImageDataUrl = null;
            }
            this.canvas.setBackgroundImage(newImg, () => {
              this.canvas.renderAll();
              eventBus.emit('bgimage:changed', true);
            }, {
              scaleX: cw / newImg.width,
              scaleY: ch / newImg.height,
              left: 0,
              top: 0,
              originX: 'left',
              originY: 'top',
            });
          }
        }, { crossOrigin: 'anonymous' });
      } else if (src && src.startsWith('data:')) {
        // Bereits Data-URL — direkt cachen
        this._bgImageDataUrl = src;
      }
    }

    // Fix CORS on all image objects loaded from JSON
    this.canvas.getObjects('image').forEach((img) => {
      const el = img.getElement ? img.getElement() : null;
      if (el && !el.crossOrigin && el.src && !el.src.startsWith('data:')) {
        el.crossOrigin = 'anonymous';
        el.src = el.src; // force reload with CORS
      }
    });

    this._redrawOverlays();
    this.canvas.renderAll();
  }

  // --- Zoom (CSS-based, not Fabric internal) ---
  // Zoom is handled via CSS transform in CanvasArea component.
  // Fabric canvas stays at 1:1 pixel ratio for accurate editing.

  // --- Misc ---

  /**
   * Konvertiert alle externen Bild-URLs in Data-URLs (base64).
   * Nutzt ein temporäres Canvas um CORS zu umgehen.
   * Falls CORS blockiert, wird das Bild über einen Backend-Proxy geladen.
   */
  async _convertImagesToDataURLs() {
    const images = this.canvas.getObjects('image');
    for (const img of images) {
      const el = img.getElement ? img.getElement() : null;
      if (!el) continue;
      const src = img.getSrc ? img.getSrc() : (el.src || '');
      if (!src || src.startsWith('data:')) continue;

      console.log('[CORS-Fix] Konvertiere Bild zu Data-URL:', src.substring(0, 60));

      // Bild komplett neu laden per fetch (umgeht tainted canvas)
      const dataUrl = await this._fetchImageAsDataURL(src);
      if (dataUrl) {
        await new Promise((resolve) => {
          img.setSrc(dataUrl, () => {
            this.canvas.renderAll();
            resolve();
          });
        });
        console.log('[CORS-Fix] Bild konvertiert');
      } else {
        console.warn('[CORS-Fix] Konnte Bild nicht laden:', src.substring(0, 60));
      }
    }

    // Hintergrundbild
    const bgImg = this.canvas.backgroundImage;
    if (bgImg && bgImg.getElement) {
      const bgSrc = bgImg.getSrc ? bgImg.getSrc() : (bgImg.getElement().src || '');
      if (bgSrc && !bgSrc.startsWith('data:')) {
        const dataUrl = await this._fetchImageAsDataURL(bgSrc);
        if (dataUrl) this._bgImageDataUrl = dataUrl;
      }
    }
  }

  /**
   * Lädt ein Bild als Data-URL — versucht erst fetch mit CORS,
   * dann Fallback über Backend-Proxy
   */
  async _fetchImageAsDataURL(url) {
    // Versuch 1: Direkter Fetch mit CORS
    try {
      const resp = await fetch(url, { mode: 'cors' });
      if (resp.ok) {
        const blob = await resp.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) { /* CORS blocked */ }

    // Versuch 2: Backend-Proxy (mehrere Pfade versuchen)
    const loc = window.location;
    const path = loc.pathname.replace(/\/+$/, '');
    const possibleProxies = [
      `${loc.origin}${path}/backend/api/proxy-image.php`,
      `${loc.origin}${path}/proxy-image.php`,
    ];
    for (const proxyBase of possibleProxies) {
      const proxyUrl = `${proxyBase}?url=${encodeURIComponent(url)}`;
    try {
      const resp = await fetch(proxyUrl);
      if (resp.ok) {
        const blob = await resp.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) { /* this proxy path failed, try next */ }
    }

    return null;
  }

  _getDesignerBaseUrl() {
    const loc = window.location;
    const path = loc.pathname.replace(/\/+$/, '');
    // /selenny-designer oder /designer
    const base = path.includes('selenny-designer') ? '/selenny-designer' : '/designer';
    return `${loc.origin}${base}/backend`;
  }

  deselectAll() {
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
  }

  /** Virtual (full-resolution) canvas dimensions in px */
  _virtualSize() {
    return { width: this.vWidth, height: this.vHeight };
  }

  /** Hide all overlay/helper objects (bleed lines, grommets, grid, etc.) */
  _hideOverlays() {
    const overlays = this.canvas.getObjects().filter((o) => o.excludeFromExport || o.isGrid || o.isBleed || o.isHem || o.isHohlsaum || o.isGrommet || o.isSnapLine || o.isGuide);
    overlays.forEach((o) => o.set('visible', false));
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
    return overlays;
  }

  _showOverlays(overlays) {
    overlays.forEach((o) => o.set('visible', true));
    this.canvas.renderAll();
  }

  toDataURL(options = {}) {
    const overlays = this._hideOverlays();
    const url = this.canvas.toDataURL({
      format: options.format || 'png',
      quality: options.quality || 1,
      multiplier: (options.multiplier || 1) / this.internalScale,
    });
    this._showOverlays(overlays);
    return url;
  }

  /**
   * Export as SVG with bleed margin. Text stays as vector (sharp at any size).
   * Returns an SVG string with dimensions = product + 2*bleed.
   * The design content is offset by bleedCm so it sits centered in the bleed area.
   */
  /**
   * Export as SVG with bleed. Text stays as vector (sharp at any size).
   * The design is scaled to fill the total area (product + 2*bleed),
   * matching how the Druckdaten app scales content to include bleed.
   * Returns SVG string with dimensions in cm.
   */
  toExportSVG() {
    const overlays = this._hideOverlays();

    const designW = this.vWidth;
    const designH = this.vHeight;

    // Total dimensions in cm (product + bleed on each side)
    const totalWCm = this.widthCm + 2 * this.bleedCm;
    const totalHCm = this.heightCm + 2 * this.bleedCm;

    // Temporarily reset zoom to 1 so Fabric's toSVG() serializes objects
    // at their full virtual coordinates, not scaled down by internalScale.
    const savedZoom = this.canvas.getZoom();
    if (this.internalScale < 1) {
      this.canvas.setZoom(1);
    }

    // Get SVG from Fabric (design area only)
    const fabricSvg = this.canvas.toSVG({
      viewBox: { x: 0, y: 0, width: designW, height: designH },
      width: designW + 'px',
      height: designH + 'px',
    });

    // Restore original zoom
    if (this.internalScale < 1) {
      this.canvas.setZoom(savedZoom);
    }

    this._showOverlays(overlays);

    const innerMatch = fabricSvg.match(/<svg[^>]*>([\s\S]*)<\/svg>/i);
    let innerContent = innerMatch ? innerMatch[1] : '';

    // Fabric.js fuegt eigene <rect> fuer backgroundColor und evtl. <image> fuer
    // backgroundImage als erste Elemente ein. Diese entfernen, da wir sie selbst
    // einfuegen. Frueher wurde "alles vor dem ersten <g>" entfernt — das hat Designs
    // mit nur Text (kein <g>) komplett geleert, weil Fabric Text als <text> exportiert.
    // Jetzt: nur die bekannten Background-Elemente gezielt entfernen.
    innerContent = innerContent.replace(
      /^\s*<rect\s+x="0"\s+y="0"\s+width="[^"]*"\s+height="[^"]*"[^>]*\/>\s*/,
      ''
    );
    innerContent = innerContent.replace(
      /^\s*<image[^>]*\/>\s*/,
      ''
    );

    // Warn if SVG content is suspiciously small (likely empty/broken)
    const userObjects = this.canvas.getObjects().filter(
      (o) => !o.excludeFromExport && !o.isGrid && !o.isBleed && !o.isHem && !o.isHohlsaum && !o.isGrommet && !o.isSnapLine && !o.isGuide
    );
    if (userObjects.length > 0 && innerContent.trim().length < 200) {
      console.warn(
        `[toExportSVG] SVG inner content is only ${innerContent.trim().length} bytes ` +
        `but canvas has ${userObjects.length} user objects. SVG may be incomplete. ` +
        `Canvas: ${this.widthCm}x${this.heightCm}cm, internalScale=${this.internalScale}`
      );
    }

    // Hintergrundbild als eingebettetes <image> in die SVG einfuegen
    // Fabric.js toSVG() exportiert backgroundImage NICHT — manuell einbetten
    let bgImageSvg = '';
    if (this._bgImageDataUrl) {
      bgImageSvg = `<image xlink:href="${this._bgImageDataUrl}" x="0" y="0" width="${designW}" height="${designH}" preserveAspectRatio="none"/>`;
    }

    // The design viewBox stays as designW x designH but the SVG physical size
    // is totalWCm x totalHCm. This means the design is uniformly scaled up
    // to fill the larger area including bleed (same as Druckdaten app LANCZOS upscale).
    const svg = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${totalWCm}cm" height="${totalHCm}cm"
     viewBox="0 0 ${designW} ${designH}">
  <desc>Druckdatei ${this.widthCm}x${this.heightCm}cm + ${this.bleedCm}cm Anschnitt umlaufend = ${totalWCm}x${totalHCm}cm</desc>
  <rect x="0" y="0" width="${designW}" height="${designH}" fill="${this.canvas.backgroundColor || '#ffffff'}"/>
  ${bgImageSvg}
  ${innerContent}
</svg>`;

    return svg;
  }

  /**
   * Export as PNG/JPEG at target DPI with bleed. The design is upscaled to fill
   * the total area (product + bleed), matching how the Druckdaten app works.
   *
   * For very large designs (e.g. 337x174cm), the target pixel dimensions at
   * 300 DPI (~40000x20000px) far exceed browser canvas limits. This method:
   * 1. Caps each dimension at MAX_PX (16384)
   * 2. Caps total pixel area at MAX_AREA (67M px, ~268MB RGBA) to avoid OOM
   * 3. Auto-reduces effective DPI for very large canvases
   * 4. Uses JPEG for large outputs to keep base64 payload manageable
   * 5. Logs the effective DPI so callers know what they got
   */
  async toExportWithBleed(options = {}) {
    // CORS-Fix: Alle externen Bilder vor dem Export in Data-URLs umwandeln
    // So wird der Canvas nicht "tainted" und toDataURL funktioniert
    await this._convertImagesToDataURLs();

    const overlays = this._hideOverlays();
    const requestedDpi = options.dpi || 300;

    // Target pixel dimensions at requested DPI (total = product + 2*bleed)
    const totalWCm = this.widthCm + 2 * this.bleedCm;
    const totalHCm = this.heightCm + 2 * this.bleedCm;
    let targetW = Math.round(totalWCm / 2.54 * requestedDpi);
    let targetH = Math.round(totalHCm / 2.54 * requestedDpi);

    // --- Guard 1: per-dimension limit (browser canvas hard limit) ---
    const MAX_PX = 16384;
    if (targetW > MAX_PX || targetH > MAX_PX) {
      const scale = Math.min(MAX_PX / targetW, MAX_PX / targetH);
      targetW = Math.round(targetW * scale);
      targetH = Math.round(targetH * scale);
    }

    // --- Guard 2: total pixel area limit ---
    // Each pixel = 4 bytes (RGBA). 67M px = ~268MB which is a safe upper bound
    // for most browsers. Beyond this, canvas creation or toDataURL will fail.
    const MAX_AREA = 67_108_864; // 8192 * 8192 equivalent
    const area = targetW * targetH;
    if (area > MAX_AREA) {
      const areaScale = Math.sqrt(MAX_AREA / area);
      targetW = Math.round(targetW * areaScale);
      targetH = Math.round(targetH * areaScale);
    }

    // Calculate effective DPI for logging
    const effectiveDpi = Math.round(targetW / (totalWCm / 2.54));
    if (effectiveDpi < requestedDpi) {
      console.info(
        `[toExportWithBleed] DPI reduced from ${requestedDpi} to ~${effectiveDpi} ` +
        `for ${this.widthCm}x${this.heightCm}cm design (target ${targetW}x${targetH}px). ` +
        `SVG export will retain full vector quality.`
      );
    }

    // Use JPEG for large canvases: PNG toDataURL on a 16384x8000 canvas produces
    // a base64 string of 200-600MB which can't be sent as JSON. JPEG at quality
    // 0.92 is typically 5-20x smaller.
    const isLargeExport = (targetW * targetH) > 25_000_000; // > 25M pixels
    const format = isLargeExport ? 'jpeg' : 'png';
    const quality = isLargeExport ? 0.92 : 1;

    // Render design from Fabric at the highest multiplier we can.
    // vWidth/vHeight are the virtual canvas dims in Fabric px (not scaled by internalScale).
    // Fabric's toDataURL applies multiplier to the actual (zoomed) canvas, so we must
    // divide by internalScale to get back to virtual-pixel output.
    const multiplier = Math.min(
      targetW / this.vWidth,
      targetH / this.vHeight,
      MAX_PX / this.vWidth,
      MAX_PX / this.vHeight
    ) / this.internalScale;

    let designUrl;
    try {
      designUrl = this.canvas.toDataURL({ format, quality, multiplier });
    } catch (err) {
      console.error('[toExportWithBleed] Fabric toDataURL failed:', err);
      this._showOverlays(overlays);
      return Promise.reject(new Error(`Fabric toDataURL failed: ${err.message}`));
    }
    this._showOverlays(overlays);

    // Sanity check: toDataURL can silently return a tiny placeholder on failure
    if (!designUrl || designUrl.length < 100) {
      console.error('[toExportWithBleed] Fabric toDataURL returned empty/invalid data');
      return Promise.reject(new Error('Fabric toDataURL returned empty data — canvas may be too large'));
    }

    // Create output canvas at target dimensions
    const offscreen = document.createElement('canvas');
    offscreen.width = targetW;
    offscreen.height = targetH;
    const ctx = offscreen.getContext('2d');
    if (!ctx) {
      console.error(`[toExportWithBleed] Failed to get 2D context for ${targetW}x${targetH} offscreen canvas`);
      return Promise.reject(new Error(`Cannot create ${targetW}x${targetH} offscreen canvas`));
    }
    ctx.fillStyle = this.canvas.backgroundColor || '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Scale design to fill ENTIRE area including bleed (uniform upscale)
          // This is how the Druckdaten app does it: img.resize((w + 2*bp, h + 2*bp), LANCZOS)
          ctx.drawImage(img, 0, 0, targetW, targetH);

          const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
          const resultUrl = offscreen.toDataURL(mimeType, quality);

          // Check for silent failure (some browsers return a short string on OOM)
          if (!resultUrl || resultUrl.length < 100) {
            reject(new Error(`offscreen toDataURL returned empty data (${targetW}x${targetH}px)`));
            return;
          }

          // Warn if payload is very large (> 50MB base64 ~ 37MB binary)
          const sizeMB = (resultUrl.length / 1_048_576).toFixed(1);
          if (resultUrl.length > 50_000_000) {
            console.warn(
              `[toExportWithBleed] Output data URL is ${sizeMB}MB — ` +
              `JSON POST may fail or be very slow. Consider using SVG export instead.`
            );
          } else {
            console.info(`[toExportWithBleed] Export complete: ${targetW}x${targetH}px, ~${effectiveDpi} DPI, ${format.toUpperCase()}, ${sizeMB}MB`);
          }

          resolve(resultUrl);
        } catch (err) {
          console.error('[toExportWithBleed] drawImage/toDataURL failed:', err);
          reject(new Error(`Export compositing failed: ${err.message}`));
        }
      };
      img.onerror = (err) => {
        console.error('[toExportWithBleed] Failed to load intermediate image from Fabric toDataURL');
        reject(new Error('Failed to load intermediate image for export compositing'));
      };
      img.src = designUrl;
    });
  }

  getActiveObject() {
    return this.canvas.getActiveObject();
  }

  getObjects() {
    return this.canvas.getObjects().filter((o) => !o.isGrid && !o.isBleed && !o.isHem && !o.isHohlsaum && !o.isGrommet);
  }

  dispose() {
    if (this.canvas) {
      this.canvas.dispose();
      this.canvas = null;
    }
  }

  // ─── Collage / Photo Grid ─────────────────────────────────────────

  /**
   * Creates a photo collage on the canvas.
   * @param {Array} layout - Array of placeholder objects { x, y, width, height, shape, rotation, label }
   *   x, y, width, height are in percentage of canvas (0-100). shape: 'rect'|'circle'|'rounded'
   * @param {Object} options - { gapPx, borderColor, borderWidth }
   * @returns {string} collageId
   */
  addCollage(layoutOrResult, options = {}) {
    const { borderColor = '#e2e8f0', borderWidth = 1 } = options;
    const collageId = Date.now().toString(36);
    const vW = this.vWidth;
    const vH = this.vHeight;

    // Check if this is a number collage (enriched result from generateNumberLayout)
    const isNumber = layoutOrResult.isNumberCollage || options.isNumberCollage;
    const layout = isNumber ? layoutOrResult.layout : layoutOrResult;

    if (isNumber) {
      // === NUMBER COLLAGE ===
      // Dense grid covering MORE than the text bounding box (with overflow).
      // Every cell gets clipPath = text shape — clipPath hides everything outside.
      // This guarantees zero gaps: the grid always extends beyond the text edges.
      const { numberText, fontFamily } = layoutOrResult;
      const showOutline = options.showOutline === true; // default OFF (clean photo look)
      const ff = fontFamily || 'Impact, Arial Black, sans-serif';

      // Measure text to find optimal font size
      const targetFontSize = vH * 0.92;
      const tempText = new fabric.Text(numberText, {
        fontFamily: ff, fontWeight: '900', fontSize: targetFontSize, fill: '#000',
      });
      let actualFontSize = targetFontSize;
      if (tempText.width > vW * 0.92) {
        actualFontSize *= (vW * 0.92) / tempText.width;
      }

      // Create the clipPath text (centered on canvas)
      const clipText = new fabric.Text(numberText, {
        fontFamily: ff,
        fontWeight: '900',
        fontSize: actualFontSize,
        fill: '#000000',
        originX: 'center',
        originY: 'center',
        left: vW / 2,
        top: vH / 2,
        absolutePositioned: true,
      });

      // Get the text bounding box — then EXPAND it 15% in all directions
      // to guarantee complete coverage (clipPath trims the excess)
      const textW = clipText.width;
      const textH = clipText.height;
      const expandX = textW * 0.15;
      const expandY = textH * 0.15;
      const gridLeft = vW / 2 - textW / 2 - expandX;
      const gridTop = vH / 2 - textH / 2 - expandY;
      const gridW = textW + expandX * 2;
      const gridH = textH + expandY * 2;

      // Build grid: ~6 rows of roughly square cells
      const targetRows = 6;
      const cellSize = gridH / targetRows;
      const cols = Math.max(2, Math.ceil(gridW / cellSize));
      const rows = targetRows;
      const actualCellW = gridW / cols;
      const actualCellH = gridH / rows;
      const gapPx = Math.max(2, actualFontSize * 0.004); // thin white gap

      // Place ALL cells — clipPath clips to number shape
      let labelIdx = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const phLeft = gridLeft + c * actualCellW + gapPx / 2;
          const phTop = gridTop + r * actualCellH + gapPx / 2;
          const phW = actualCellW - gapPx;
          const phH = actualCellH - gapPx;

          const placeholder = new fabric.Rect({
            left: phLeft,
            top: phTop,
            width: phW,
            height: phH,
            fill: '#dde1e7',
            stroke: '#ffffff',
            strokeWidth: Math.max(1, gapPx * 0.5),
            selectable: false,
            evented: true,
            hoverCursor: 'pointer',
            clipPath: clipText,
            isCollagePlaceholder: true,
            isNumberCollagePlaceholder: true,
            _numberClipText: clipText,
            collageIndex: labelIdx,
            collageId: collageId,
            collageShape: 'rect',
          });
          this.canvas.add(placeholder);

          const labelText = new fabric.Text(String(labelIdx + 1), {
            fontSize: Math.min(phW, phH) * 0.28,
            fill: '#9aa5b4',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            originX: 'center',
            originY: 'center',
            left: phLeft + phW / 2,
            top: phTop + phH / 2,
            clipPath: clipText,
            selectable: false,
            evented: true,
            hoverCursor: 'pointer',
            isCollageLabel: true,
            collageIndex: labelIdx,
            collageId: collageId,
          });
          this.canvas.add(labelText);
          labelIdx++;
        }
      }

      // Optional outline text on top (default OFF for clean photo-only look)
      if (showOutline) {
        const outlineText = new fabric.Text(numberText, {
          fontFamily: ff,
          fontWeight: '900',
          fontSize: actualFontSize,
          fill: 'transparent',
          stroke: '#222222',
          strokeWidth: Math.max(6, actualFontSize * 0.04),
          strokeLineJoin: 'round',
          originX: 'center',
          originY: 'center',
          left: vW / 2,
          top: vH / 2,
          selectable: false,
          evented: false,
          isNumberOutline: true,
          collageId: collageId,
          excludeFromExport: false,
        });
        this.canvas.add(outlineText);
      }

      this._bringOverlaysToFront();
      this.canvas.renderAll();
      eventBus.emit('collage:created', { collageId, placeholderCount: labelIdx });
      return collageId;

    } else {
      // === GRID COLLAGE (original behavior) ===
      // Store layout for later gap adjustments
      this._collageLayouts[collageId] = {
        templateCells: layout.map(ph => ({ x: ph.x, y: ph.y, width: ph.width, height: ph.height, shape: ph.shape, rotation: ph.rotation, label: ph.label })),
      };
      layout.forEach((ph, i) => {
        const pxX = (ph.x / 100) * vW;
        const pxY = (ph.y / 100) * vH;
        const pxW = (ph.width / 100) * vW;
        const pxH = (ph.height / 100) * vH;
        const rotation = ph.rotation || 0;
        const label = ph.label || String(i + 1);

        let placeholder;
        if (ph.shape === 'circle') {
          const r = Math.min(pxW, pxH) / 2;
          placeholder = new fabric.Circle({
            left: pxX + pxW / 2 - r,
            top: pxY + pxH / 2 - r,
            radius: r,
            fill: '#f1f5f9',
            stroke: borderColor,
            strokeWidth: borderWidth,
            selectable: false,
            evented: true,
            hoverCursor: 'pointer',
            angle: rotation,
            isCollagePlaceholder: true,
            collageIndex: i,
            collageId: collageId,
            collageShape: 'circle',
          });
        } else {
          const rx = ph.shape === 'rounded' ? 15 : 0;
          placeholder = new fabric.Rect({
            left: pxX,
            top: pxY,
            width: pxW,
            height: pxH,
            rx: rx,
            ry: rx,
            fill: '#f1f5f9',
            stroke: borderColor,
            strokeWidth: borderWidth,
            selectable: false,
            evented: true,
            hoverCursor: 'pointer',
            angle: rotation,
            isCollagePlaceholder: true,
            collageIndex: i,
            collageId: collageId,
            collageShape: ph.shape || 'rect',
          });
        }
        this.canvas.add(placeholder);

        // Label text centered on placeholder
        const labelText = new fabric.Text(label, {
          fontSize: Math.min(pxW, pxH) * 0.35,
          fill: '#94a3b8',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'bold',
          originX: 'center',
          originY: 'center',
          left: pxX + pxW / 2,
          top: pxY + pxH / 2,
          selectable: false,
          evented: false,
          isCollageLabel: true,
          collageIndex: i,
          collageId: collageId,
        });
        this.canvas.add(labelText);
      });
    }

    this._bringOverlaysToFront();
    this.canvas.renderAll();
    eventBus.emit('collage:created', { collageId, placeholderCount: layout.length });
    return collageId;
  }

  /**
   * Update the gap (spacing) of an existing grid collage.
   * Repositions all placeholders, labels, and images.
   * @param {string} collageId
   * @param {Array} newLayoutCells - recalculated cells [{ x, y, width, height }] in %
   */
  updateCollageLayout(collageId, newLayoutCells) {
    const vW = this.vWidth;
    const vH = this.vHeight;
    const objects = this.canvas.getObjects();

    newLayoutCells.forEach((cell, i) => {
      const pxX = (cell.x / 100) * vW;
      const pxY = (cell.y / 100) * vH;
      const pxW = (cell.width / 100) * vW;
      const pxH = (cell.height / 100) * vH;

      // Update placeholder
      const ph = objects.find(o => o.isCollagePlaceholder && o.collageId === collageId && o.collageIndex === i);
      if (ph) {
        if (ph.type === 'circle') {
          const r = Math.min(pxW, pxH) / 2;
          ph.set({ left: pxX + pxW / 2 - r, top: pxY + pxH / 2 - r, radius: r });
        } else {
          ph.set({ left: pxX, top: pxY, width: pxW, height: pxH });
        }
        ph.setCoords();
      }

      // Update label
      const label = objects.find(o => o.isCollageLabel && o.collageId === collageId && o.collageIndex === i);
      if (label) {
        label.set({
          left: pxX + pxW / 2,
          top: pxY + pxH / 2,
          fontSize: Math.min(pxW, pxH) * 0.35,
        });
        label.setCoords();
      }

      // Update collage image if present
      const img = objects.find(o => o.isCollageImage && o.collageId === collageId && o.collageIndex === i);
      if (img && ph) {
        // Re-fit image into new placeholder bounds
        const scaleX = pxW / img.width;
        const scaleY = pxH / img.height;
        const scale = Math.max(scaleX, scaleY);
        img.set({
          scaleX: scale,
          scaleY: scale,
          left: pxX + pxW / 2,
          top: pxY + pxH / 2,
          originX: 'center',
          originY: 'center',
        });
        // Update clipPath if rect-based
        if (img.clipPath && img.clipPath.type === 'rect') {
          img.clipPath.set({
            left: pxX,
            top: pxY,
            width: pxW,
            height: pxH,
          });
        }
        img.setCoords();
      }
    });

    // Update stored layout
    if (this._collageLayouts[collageId]) {
      this._collageLayouts[collageId].templateCells = newLayoutCells.map(c => ({ ...c }));
    }

    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  /**
   * Get the stored template cells for a collage (for gap recalculation).
   */
  getCollageLayout(collageId) {
    return this._collageLayouts[collageId]?.templateCells || null;
  }

  /**
   * Sets an image into a collage placeholder.
   * @param {string} collageId
   * @param {number} index - placeholder index
   * @param {string} imageUrl
   * @returns {Promise<void>}
   */
  setCollageImage(collageId, index, imageUrl) {
    return new Promise((resolve, reject) => {
      const objects = this.canvas.getObjects();
      const placeholder = objects.find(
        (o) => o.isCollagePlaceholder && o.collageId === collageId && o.collageIndex === index
      );
      if (!placeholder) {
        reject(new Error(`Collage placeholder not found: ${collageId} #${index}`));
        return;
      }

      fabric.Image.fromURL(imageUrl, (img) => {
        if (!img) {
          reject(new Error('Failed to load image'));
          return;
        }

        // Determine placeholder bounds
        let phLeft, phTop, phW, phH;
        if (placeholder.collageShape === 'circle') {
          const r = placeholder.radius;
          phLeft = placeholder.left;
          phTop = placeholder.top;
          phW = r * 2;
          phH = r * 2;
        } else {
          phLeft = placeholder.left;
          phTop = placeholder.top;
          phW = placeholder.width * (placeholder.scaleX || 1);
          phH = placeholder.height * (placeholder.scaleY || 1);
        }

        // Scale image to COVER the placeholder
        const scaleX = phW / img.width;
        const scaleY = phH / img.height;
        const coverScale = Math.max(scaleX, scaleY);

        img.set({
          scaleX: coverScale,
          scaleY: coverScale,
          originX: 'center',
          originY: 'center',
          left: phLeft + phW / 2,
          top: phTop + phH / 2,
          selectable: true,
          evented: true,
          isCollageImage: true,
          collageId: collageId,
          collageIndex: index,
        });

        // Create clipPath matching the placeholder shape
        if (placeholder.isNumberCollagePlaceholder && placeholder._numberClipText) {
          // Number collage: clip image to the text shape so nothing extends outside the number
          img.clipPath = placeholder._numberClipText;
        } else if (placeholder.collageShape === 'circle') {
          const r = placeholder.radius;
          img.clipPath = new fabric.Circle({
            radius: r,
            left: phLeft,
            top: phTop,
            absolutePositioned: true,
          });
        } else {
          const rx = placeholder.collageShape === 'rounded' ? 15 : 0;
          img.clipPath = new fabric.Rect({
            left: phLeft,
            top: phTop,
            width: phW,
            height: phH,
            rx: rx,
            ry: rx,
            absolutePositioned: true,
          });
        }

        // Remove label for this slot
        const label = this.canvas.getObjects().find(
          (o) => o.isCollageLabel && o.collageId === collageId && o.collageIndex === index
        );
        if (label) this.canvas.remove(label);

        // Keep placeholder behind as background, add image on top
        this.canvas.add(img);
        this._bringOverlaysToFront();
        this.canvas.renderAll();
        resolve();
      }, { crossOrigin: 'anonymous' });
    });
  }

  /**
   * Recalculates clipPath for a collage image after movement.
   * Since clipPath is absolutePositioned, it stays fixed while the user drags the image
   * behind the mask. This is the desired behavior - no recalculation needed.
   * @param {string} collageId
   * @param {number} index
   */
  updateCollageImageClip(collageId, index) {
    // clipPath is absolutePositioned so it stays fixed while the image moves.
    // This is intentional: the user repositions the image behind the mask.
    // No-op by design.
  }

  /**
   * Removes all objects belonging to a collage.
   * @param {string} collageId
   */
  removeCollage(collageId) {
    const toRemove = this.canvas.getObjects().filter((o) => o.collageId === collageId);
    toRemove.forEach((o) => this.canvas.remove(o));
    this.canvas.renderAll();
  }

  /**
   * Clears all user-added objects from the canvas (keeps bleed, hem, grommets, grid overlays).
   */
  clearCanvas() {
    const overlayIds = new Set([
      ...this.bleedObjects, ...this.hemObjects,
      ...this.hohlsaumObjects, ...this.grommetsObjects, ...this.gridObjects,
    ]);
    const toRemove = this.canvas.getObjects().filter((o) =>
      !overlayIds.has(o) && !o.isBleed && !o.isHem && !o.isHohlsaum && !o.isGrommet && !o.isGrid && !o.isSnapLine && !o.isGuide
    );
    toRemove.forEach((o) => this.canvas.remove(o));
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
    eventBus.emit('canvas:changed');
  }

  /**
   * Generates a grid layout array for N images.
   * @param {number} count - number of images
   * @param {string} shape - 'rect', 'circle', or 'rounded'
   * @param {number} gapPercent - gap between cells in percentage (default 2)
   * @returns {Array} layout array for addCollage()
   */
  generateGridLayout(count, shape = 'rect', gapPercent = 2) {
    const aspectRatio = this.vWidth / this.vHeight;
    const cols = Math.ceil(Math.sqrt(count * aspectRatio));
    const rows = Math.ceil(count / cols);
    const cellW = (100 - (cols + 1) * gapPercent) / cols;
    const cellH = (100 - (rows + 1) * gapPercent) / rows;

    const layout = [];
    let idx = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (idx >= count) break;
        let w = cellW;
        let h = cellH;
        if (shape === 'circle') {
          const side = Math.min(cellW, cellH);
          w = side;
          h = side;
        }
        const x = gapPercent + c * (cellW + gapPercent) + (cellW - w) / 2;
        const y = gapPercent + r * (cellH + gapPercent) + (cellH - h) / 2;
        layout.push({
          x,
          y,
          width: w,
          height: h,
          shape: shape,
          rotation: 0,
          label: String(idx + 1),
        });
        idx++;
      }
    }
    return layout;
  }

  /**
   * Generates placeholders arranged in the shape of a digit/number string.
   * @param {string} number - e.g. "50", "30", "18"
   * @param {string} shape - 'circle' or 'rounded'
   * @param {number} fontSize - not used directly, placeholder for future use
   * @returns {Array} layout array for addCollage()
   */
  generateNumberLayout(number, shape = 'rect', options = {}) {
    const numberStr = String(number);
    const { cellsPerRow = 0 } = options;

    // Use offscreen canvas to determine which grid cells fall inside the text shape
    const offCanvas = document.createElement('canvas');
    const offCtx = offCanvas.getContext('2d');
    const canvasAspect = this.vWidth / this.vHeight;

    // Offscreen canvas size (large enough for accurate sampling)
    const offW = 800;
    const offH = Math.round(offW / canvasAspect);
    offCanvas.width = offW;
    offCanvas.height = offH;

    // Render text centered, as large as possible
    offCtx.clearRect(0, 0, offW, offH);
    const fontFamily = 'Impact, Arial Black, sans-serif';

    // Find optimal font size to fill ~85% of the canvas
    let fontSize = offH * 0.85;
    offCtx.font = `900 ${fontSize}px ${fontFamily}`;
    let metrics = offCtx.measureText(numberStr);
    // Scale down if too wide
    if (metrics.width > offW * 0.9) {
      fontSize *= (offW * 0.9) / metrics.width;
      offCtx.font = `900 ${fontSize}px ${fontFamily}`;
      metrics = offCtx.measureText(numberStr);
    }

    // Draw text centered
    const textX = (offW - metrics.width) / 2;
    const textAscent = metrics.actualBoundingBoxAscent || fontSize * 0.75;
    const textDescent = metrics.actualBoundingBoxDescent || fontSize * 0.15;
    const textHeight = textAscent + textDescent;
    const textY = (offH + textAscent - textDescent) / 2;

    offCtx.fillStyle = '#000000';
    offCtx.fillText(numberStr, textX, textY);

    // Get text bounding box from the rendered pixels
    const imageData = offCtx.getImageData(0, 0, offW, offH);
    const pixels = imageData.data;

    let minX = offW, maxX = 0, minY = offH, maxY = 0;
    for (let py = 0; py < offH; py++) {
      for (let px = 0; px < offW; px++) {
        if (pixels[(py * offW + px) * 4 + 3] > 128) {
          if (px < minX) minX = px;
          if (px > maxX) maxX = px;
          if (py < minY) minY = py;
          if (py > maxY) maxY = py;
        }
      }
    }

    // Add small padding
    const pad = 2;
    minX = Math.max(0, minX - pad);
    maxX = Math.min(offW - 1, maxX + pad);
    minY = Math.max(0, minY - pad);
    maxY = Math.min(offH - 1, maxY + pad);

    const textBoxW = maxX - minX;
    const textBoxH = maxY - minY;

    // Determine grid: more rows = denser fill. Use 8 rows for tight packing.
    const targetRows = 8;
    const cellSizePx = Math.round(textBoxH / targetRows);
    const cols = Math.max(2, Math.ceil(textBoxW / cellSizePx));
    const rows = Math.max(2, Math.ceil(textBoxH / cellSizePx));
    const actualCellW = textBoxW / cols;
    const actualCellH = textBoxH / rows;

    // Thin white gap between cells (just a thin line, not a big spacing)
    const gapPx = 2; // fixed 2px gap for a subtle white line between photos
    const innerCellW = actualCellW - gapPx;
    const innerCellH = actualCellH - gapPx;

    // Test each grid cell: include if ANY significant portion overlaps the text shape.
    // Sample multiple points across the cell, not just center.
    const layout = [];
    let labelIdx = 1;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cellLeft = minX + c * actualCellW;
        const cellTop = minY + r * actualCellH;

        // Sample a 3x3 grid of points within the cell
        let hitCount = 0;
        const samplePoints = 3;
        for (let sy = 0; sy < samplePoints; sy++) {
          for (let sx = 0; sx < samplePoints; sx++) {
            const sampleX = Math.round(cellLeft + (sx + 0.5) * (actualCellW / samplePoints));
            const sampleY = Math.round(cellTop + (sy + 0.5) * (actualCellH / samplePoints));
            if (sampleX >= 0 && sampleX < offW && sampleY >= 0 && sampleY < offH) {
              const alpha = pixels[(sampleY * offW + sampleX) * 4 + 3];
              if (alpha > 128) hitCount++;
            }
          }
        }

        // Include cell if at least 2 out of 9 sample points are inside the text
        // (clipPath will crop anything outside anyway)
        if (hitCount >= 2) {
          const finalLeft = cellLeft + gapPx / 2;
          const finalTop = cellTop + gapPx / 2;

          layout.push({
            x: (finalLeft / offW) * 100,
            y: (finalTop / offH) * 100,
            width: (innerCellW / offW) * 100,
            height: (innerCellH / offH) * 100,
            shape: shape,
            rotation: 0,
            label: String(labelIdx++),
          });
        }
      }
    }

    // Return enriched result for number collage
    return {
      layout,
      isNumberCollage: true,
      numberText: numberStr,
      fontFamily: fontFamily,
      fontSize: fontSize,
      textBounds: {
        x: (minX / offW) * 100,
        y: (minY / offH) * 100,
        width: (textBoxW / offW) * 100,
        height: (textBoxH / offH) * 100,
      },
      offW,
      offH,
    };
  }
}

export default CanvasManager;
export { PX_PER_CM };
